/**
 * Polyfills :focus-visible for non supporting browsers (Safari < 15.4).
 */
function focusVisiblePolyfill() {
  const navKeys = ['Tab', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter', 'Space', 'Escape', 'Home', 'End', 'PageUp', 'PageDown'];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (evt) => {
    if (navKeys.includes(evt.code)) mouseClick = false;
  });

  window.addEventListener('mousedown', () => {
    mouseClick = true;
  });

  window.addEventListener('focus', () => {
    if (currentFocusedElement) currentFocusedElement.classList.remove('is-focused');
    if (mouseClick) return;

    currentFocusedElement = document.activeElement;
    currentFocusedElement.classList.add('is-focused');
  }, true);
}

// Add polyfill if :focus-visible is not supported.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

/**
 * Creates a 'mediaMatches' object from the media queries specified in the theme,
 * and adds listeners for each media query. If a breakpoint is crossed, the mediaMatches
 * values are updated and a 'on:breakpoint-change' event is dispatched.
 */
(() => {
  const { mediaQueries } = theme;
  if (!mediaQueries) return;

  const mqKeys = Object.keys(mediaQueries);
  const mqLists = {};
  theme.mediaMatches = {};

  /**
   * Handles a media query (breakpoint) change.
   */
  const handleMqChange = () => {
    const newMatches = mqKeys.reduce((acc, media) => {
      acc[media] = !!(mqLists[media] && mqLists[media].matches);
      return acc;
    }, {});

    // Update mediaMatches values after breakpoint change.
    Object.keys(newMatches).forEach((key) => {
      theme.mediaMatches[key] = newMatches[key];
    });

    window.dispatchEvent(new CustomEvent('on:breakpoint-change'));
  };

  mqKeys.forEach((mq) => {
    // Create mqList object for each media query.
    mqLists[mq] = window.matchMedia(mediaQueries[mq]);

    // Get initial matches for each query.
    theme.mediaMatches[mq] = mqLists[mq].matches;

    // Add an event listener to each query.
    try {
      mqLists[mq].addEventListener('change', handleMqChange);
    } catch (err1) {
      // Fallback for legacy browsers (Safari < 14).
      mqLists[mq].addListener(handleMqChange);
    }
  });
})();

/**
 * Returns a function that as long as it continues to be invoked, won't be triggered.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Delay (in milliseconds).
 * @returns {Function}
 */
function debounce(fn, wait = 300) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

/**
 * Sets a 'viewport-height' custom property on the root element.
 */
function setViewportHeight() {
  document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
}

/**
 * Sets a 'header-height' custom property on the root element.
 */
function setHeaderHeight() {
  const header = document.getElementById('shopify-section-header');
  if (!header) return;
  let height = header.offsetHeight;

  // Add announcement bar height (if shown).
  const announcement = document.getElementById('shopify-section-announcement');
  if (announcement) height += announcement.offsetHeight;

  document.documentElement.style.setProperty('--header-height', `${height}px`);
}

/**
 * Sets a 'scrollbar-width' custom property on the root element.
 */
function setScrollbarWidth() {
  document.documentElement.style.setProperty(
      '--scrollbar-width',
      `${window.innerWidth - document.documentElement.clientWidth}px`
  );
}

/**
 * Sets the dimension variables.
 */
function setDimensionVariables() {
  setViewportHeight();
  setHeaderHeight();
  setScrollbarWidth();
}

// Set the dimension variables.
setDimensionVariables();

// Update the dimension variables if viewport resized.
window.addEventListener('resize', debounce(setDimensionVariables, 50));

/**
 * Checks if a lazy load image has alternate <source> elements and copies the
 * 'data-src' and 'data-srcset' attributes to 'src' and 'srcset' accordingly.
 * @param {Element} img - Image element.
 */
function setImageSources(img) {
  const setImageAttr = (el) => {
    if (el.dataset.src && !el.src) {
      el.src = el.dataset.src;
    }

    if (el.dataset.srcset && !el.srcset) {
      el.srcset = el.dataset.srcset;
    }
  };

  if (img.parentNode.tagName === 'PICTURE') {
    Array.from(img.parentNode.children).forEach((el) => {
      setImageAttr(el);
    });
  } else {
    setImageAttr(img);
  }
}

/**
 * Initialises lazy load images.
 */
function initLazyImages() {
  if ('loading' in HTMLImageElement.prototype === false && 'IntersectionObserver' in window) {
    // If native lazyload not supported but IntersectionObserver supported (Safari).
    const io = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const img = entry.target;
          setImageSources(img);
          observer.unobserve(img);
        }
      });
    }, { rootMargin: '0px 0px 500px 0px' });

    document.querySelectorAll('[loading="lazy"]').forEach((img) => {
      io.observe(img);
    });
  } else {
    // If native lazy load supported or IntersectionObserver not supported (legacy browsers).
    document.querySelectorAll('[loading="lazy"]').forEach((img) => {
      setImageSources(img);
    });
  }
}

/**
 * Adds an observer to initialise a script when an element is scrolled into view.
 * @param {Element} element - Element to observe.
 * @param {Function} callback - Function to call when element is scrolled into view.
 * @param {number} [threshold=500] - Distance from viewport (in pixels) to trigger init.
 */
function initLazyScript(element, callback, threshold = 500) {
  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          if (typeof callback === 'function') {
            callback();
            observer.unobserve(entry.target);
          }
        }
      });
    }, { rootMargin: `0px 0px ${threshold}px 0px` });

    io.observe(element);
  } else {
    callback();
  }
}

/**
 * Pauses all media (videos/models) within an element.
 * @param {Element} [el=document] - Element to pause media within.
 */
function pauseAllMedia(el = document) {
  el.querySelectorAll('.js-youtube, .js-vimeo, video').forEach((video) => {
    const component = video.closest('video-component');
    if (component && component.dataset.background === 'true') return;

    if (video.matches('.js-youtube')) {
      video.contentWindow.postMessage('{ "event": "command", "func": "pauseVideo", "args": "" }', '*');
    } else if (video.matches('.js-vimeo')) {
      video.contentWindow.postMessage('{ "method": "pause" }', '*');
    } else {
      video.pause();
    }
  });

  el.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

async function getSection(section) {
  try {
    // call the rendering API
    const response = await fetch(`/?section_id=${section}`);
    const htmlData = await response.text();
    // create a DOMParser and get the inside content of the section
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlData, 'text/html');
    const sectionContent = doc.querySelector('.shopify-section');
    if (!sectionContent) {
      console.error('No section content found');
      return {};
    }

    // Get the text content of the section, assuming it contains JSON data
    const jsonContent = sectionContent.textContent.trim();

    // Parse the JSON content
    const jsonData = JSON.parse(jsonContent);

    return jsonData;
  } catch (error) {
    console.error('Error fetching section:', error);
    return {};
  }
}

class DeferredMedia extends HTMLElement {
  constructor() {
    super();

    const loadBtn = this.querySelector('.js-load-media');
    if (loadBtn) {
      loadBtn.addEventListener('click', this.loadContent.bind(this));
    } else {
      this.addObserver();
    }

    this.addEventListener('mouseenter', () => {
      this.addObserver();
    });

    this.addEventListener('mouseleave', () => {
      if (this.closest('.video-section')) {
        this.closest('.video-section').classList.remove('video-section--played');
        this.setAttribute('loaded-video', '');
      }
    });

    this.addEventListener('mouseenter', this.playMedia.bind(this));
    this.addEventListener('mouseleave', this.pauseMedia.bind(this));

  }

  /**
   * Adds an Intersection Observer to load the content when viewport scroll is near
   */
  addObserver() {
    if (this.hasAttribute('loaded') || !('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.loadContent(false, false, 'observer');
          observer.unobserve(this);
        }
      });
    }, { rootMargin: '0px 0px 0px 0px' });

    observer.observe(this);
  }

  /**
   * Loads the deferred media.
   * @param {boolean} [focus=true] - Focus the deferred media element after loading.
   * @param {boolean} [pause=true] - Whether to pause all media after loading.
   * @param {string} [loadTrigger='click'] - The action that caused the deferred content to load.
   */
  loadContent(focus = true, pause = true, loadTrigger = 'click') {
    if (pause) pauseAllMedia();
    if (this.getAttribute('loaded') !== null) return;
    if (this.querySelector('template')){
      this.loadTrigger = loadTrigger;
      const content = this.querySelector('template').content.firstElementChild.cloneNode(true);
      this.appendChild(content);
      this.setAttribute('loaded', '');

      const deferredEl = this.querySelector('video, model-viewer, iframe');
      if (deferredEl && focus) deferredEl.focus();
      const videoElement = this.querySelector('video');
      if (videoElement && !videoElement.classList.contains('video-detail')) {
        this.setupVideoLoop(videoElement);
      }
    }

  }
  /**
   * Plays the video or media inside this DeferredMedia element.
   */
  playMedia() {
    const mediaElement = this.querySelector('video');
    if (mediaElement) { // Ensure mediaElement exists
      if (!mediaElement.classList.contains('video-detail')) {
        if (typeof mediaElement.play === 'function') {
          mediaElement.play();
          console.log('playMedia');
        }
      }
    }
  }

  /**
   * Pauses the video or media inside this DeferredMedia element.
   */
  pauseMedia() {
    const mediaElement = this.querySelector('video');
    if (mediaElement) { // Ensure mediaElement exists
      if (!mediaElement.classList.contains('video-detail')) {
        if (typeof mediaElement.pause === 'function') {
          mediaElement.pause();
          console.log('pauseMedia');
        }
      }
    }

  }
  /**
   * Sets up the video to autoplay for 5 seconds and loop.
   * @param {HTMLVideoElement} videoElement - The video element to set up.
   */
  setupVideoLoop(videoElement) {
    videoElement.addEventListener('timeupdate', () => {
      if (videoElement.currentTime >= 5) {
        videoElement.currentTime = 0; // Seek back to the start
        videoElement.play(); // Ensure it keeps playing
      }
    });
  }

}

customElements.define('deferred-media', DeferredMedia);

// Function to check if each DeferredMedia element is within the current scroll position
function checkDeferredMediaInView() {
  if($(window).innerWidth() < 768) {
    const deferredMediaElements = document.querySelectorAll('deferred-media');

    deferredMediaElements.forEach((element) => {
      if (!element.classList.contains('deferred-media-video')) {
        const rect = element.getBoundingClientRect();
        const windowHeight = window.innerHeight || document.documentElement.clientHeight;

        // If element's top is within the viewport or has been scrolled past
        if (rect.top <= windowHeight - 300 && rect.bottom >= 0) {
          element.addObserver(); // Run addObserver on the visible element
        }
      }
    });
    console.log('checkDeferredMediaInView');
  }
}

// Attach the scroll event to check for DeferredMedia elements in view
window.addEventListener('scroll', checkDeferredMediaInView);

// Initial check on page load to catch any elements already in view
checkDeferredMediaInView();

class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.disclosure = this.querySelector('details');
    this.toggle = this.querySelector('summary');
    this.panel = this.toggle.nextElementSibling;
    this.init();
  }

  init() {
    // Check if the content element has a CSS transition.
    const styles = window.getComputedStyle(this.panel);
    this.hasTransition = styles.transition !== 'all 0s ease 0s';

    if (this.hasTransition) {
      this.toggle.addEventListener('click', this.handleToggle.bind(this));
      this.disclosure.addEventListener('transitionend', this.handleTransitionEnd.bind(this));
    }
  }

  /**
   * Handles 'click' events on the summary element.
   * @param {object} evt - Event object.
   */
  handleToggle(evt) {
    evt.preventDefault();

    if (!this.disclosure.open) {
      this.open();
    } else {
      this.close();
    }
  }

  /**
   * Handles 'transitionend' events on the details element.
   * @param {object} evt - Event object.
   */
  handleTransitionEnd(evt) {
    if (evt.target !== this.panel) return;

    if (this.disclosure.classList.contains('is-closing')) {
      this.disclosure.classList.remove('is-closing');
      this.disclosure.open = false;
    }

    this.panel.removeAttribute('style');
  }

  /**
   * Adds inline 'height' style to the content element, to trigger open transition.
   */
  addContentHeight() {
    this.panel.style.height = `${this.panel.scrollHeight}px`;
  }

  /**
   * Opens the details element.
   */
  open() {
    // Set content 'height' to zero before opening the details element.
    this.panel.style.height = '0';

    // Open the details element
    this.disclosure.open = true;

    // Set content 'height' to its scroll height, to enable CSS transition.
    this.addContentHeight();
  }

  /**
   * Closes the details element.
   */
  close() {
    // Set content height to its scroll height, to enable transition to zero.
    this.addContentHeight();

    // Add class to enable styling of content or toggle icon before or during close transition.
    this.disclosure.classList.add('is-closing');

    // Set content height to zero to trigger the transition.
    // Slight delay required to allow scroll height to be applied before changing to '0'.
    setTimeout(() => {
      this.panel.style.height = '0';
    });
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

const trapFocusHandlers = {};

/**
 * Removes focus trap event listeners and optionally focuses an element.
 * @param {Element} [elementToFocus=null] - Element to focus when trap is removed.
 */
function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

/**
 * Traps focus within a container, e.g. modal or side drawer.
 * @param {Element} container - Container element to trap focus within.
 * @param {Element} [elementToFocus=container] - Initial element to focus when trap is applied.
 */
function trapFocus(container, elementToFocus = container) {
  const focusableEls = Array.from(
      container.querySelectorAll('summary, a[href], area[href], button:not([disabled]), input:not([type=hidden]):not([disabled]), select:not([disabled]), textarea:not([disabled]), object, iframe, audio[controls], video[controls], [tabindex]:not([tabindex^="-"])')
  );

  const firstEl = focusableEls[0];
  const lastEl = focusableEls[focusableEls.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (evt) => {
    if (evt.target !== container && evt.target !== lastEl && evt.target !== firstEl) return;
    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = () => {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = (evt) => {
    if (evt.code !== 'Tab') return;

    // If tab pressed on last focusable element, focus the first element.
    if (evt.target === lastEl && !evt.shiftKey) {
      evt.preventDefault();
      firstEl.focus();
    }

    //  If shift + tab pressed on the first focusable element, focus the last element.
    if ((evt.target === container || evt.target === firstEl) && evt.shiftKey) {
      evt.preventDefault();
      lastEl.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  (elementToFocus || container).focus();
}

class Modal extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', this.handleClick.bind(this));
  }

  /**
   * Handles 'click' events on the modal.
   * @param {object} evt - Event object.
   */
  handleClick(evt) {
    if (evt.target !== this && !evt.target.matches('.js-close-modal')) return;
    this.close();
  }

  /**
   * Opens the modal.
   * @param {Element} opener - Modal opener element.
   */
  open(opener) {
    // Prevent page behind from scrolling when side drawer is open
    this.scrollY = window.scrollY;
    document.body.classList.add('fixed');
    document.body.style.top = `-${this.scrollY}px`;

    this.setAttribute('open', '');
    this.openedBy = opener;

    trapFocus(this);
    window.pauseAllMedia();

    // Add event handler (so the bound event listener can be removed).
    this.keyupHandler = (evt) => evt.key === 'Escape' && this.close();

    // Add event listener (for while modal is open).
    this.addEventListener('keyup', this.keyupHandler);
  }

  /**
   * Closes the modal.
   */
  close() {
    // Restore page position and scroll behaviour.
    document.body.style.top = '';
    document.body.classList.remove('fixed');
    window.scrollTo(0, this.scrollY);

    this.removeAttribute('open');

    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();

    // Remove event listener added on modal opening.
    this.removeEventListener('keyup', this.keyupHandler);
  }
}

customElements.define('modal-dialog', Modal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');
    if (!button) return;

    button.addEventListener('click', () => {
      const modal = document.getElementById(this.dataset.modal);
      if (modal) modal.open(button);
    });
  }
}

customElements.define('modal-opener', ModalOpener);

class ProductCard extends HTMLElement {
  constructor() {
    super();
    window.initLazyScript(this, this.init.bind(this));
  }

  init() {
    this.images = this.querySelectorAll('.card__main-image');
    this.links = this.querySelectorAll('.js-prod-link');
    this.quickAddBtn = this.querySelector('.js-quick-add');

    if (this.quickAddBtn) {
      this.productUrl = this.quickAddBtn.dataset.productUrl;
    } else if (this.links.length) {
      this.productUrl = this.links[0].href;
    }

    this.addEventListener('change', this.handleSwatchChange.bind(this));
  }

  /**
   * Handles 'change' events in the product card swatches.
   * @param {object} evt - Event object.
   */
  handleSwatchChange(evt) {
    if (!evt.target.matches('.opt-btn')) return;

    // Swap current card image to selected variant image.
    if (evt.target.dataset.mediaId) {
      const variantMedia = this.querySelector(`[data-media-id="${evt.target.dataset.mediaId}"]`);

      if (variantMedia) {
        this.images.forEach((image) => { image.hidden = true; });
        variantMedia.hidden = false;
      }
    }

    const separator = this.productUrl.split('?').length > 1 ? '&' : '?';
    const url = `${this.productUrl + separator}variant=${evt.target.dataset.variantId}`;

    // Update link hrefs to url of selected variant.
    this.links.forEach((link) => {
      link.href = url;
    });

    // Update the Quick Add button data.
    if (this.quickAddBtn) {
      this.quickAddBtn.dataset.selectedColor = evt.target.value;
    }
  }
}

customElements.define('product-card', ProductCard);

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('.qty-input__input');
    this.currentQty = this.input.value;
    this.changeEvent = new Event('change', { bubbles: true });

    this.addEventListener('click', this.handleClick.bind(this));
    this.input.addEventListener('keydown', this.handleKeydown.bind(this));
  }

  /**
   * Handles 'click' events on the quantity input element.
   * @param {object} evt - Event object.
   */
  handleClick(evt) {
    if (!evt.target.matches('.qty-input__btn')) return;
    evt.preventDefault();

    if (evt.target.name === 'plus') {
      this.input.stepUp();
    } else {
      this.input.stepDown();
    }

    if (this.input.value !== this.currentQty) {
      this.input.dispatchEvent(this.changeEvent);
      this.currentQty = this.input.value;
    }
  }

  /**
   * Handles 'keydown' events on the input field.
   * @param {object} evt - Event object.
   */
  handleKeydown(evt) {
    if (evt.key !== 'Enter') return;
    evt.preventDefault();

    if (this.input.value !== this.currentQty) {
      this.input.blur();
      this.input.focus();
      this.currentQty = this.input.value;
    }
  }
}

customElements.define('quantity-input', QuantityInput);

class QtyInput extends HTMLInputElement {
  constructor() {
    super();

    this.addEventListener('input', this.onValueInput);
    this.addEventListener('change', this.onValueChanged);
    this.addEventListener('keydown', this.onKeyDown);
    this.addEventListener('focus', this.select);
  }

  get quantity() {
    return parseInt(this.value);
  }

  set quantity(quantity) {
    const isNumeric = (typeof quantity === 'number' || typeof quantity === 'string' && quantity.trim() !== '') && !isNaN(quantity);
    if (quantity === '') return;

    if (!isNumeric || quantity < 0) {
      quantity = parseInt(quantity) || 1;
    }

    this.value = Math.max(this.min || 1, Math.min(quantity, this.max || Number.MAX_VALUE)).toString();
    this.size = Math.max(this.value.length + 1, 2);
  }

  onValueInput() {
    this.quantity = this.value;
  }

  onValueChanged() {
    if (this.value === '') {
      this.quantity = 1;
    }
  }

  onKeyDown(event) {
    event.stopPropagation();

    const quantity = this.quantity;
    if (event.key === "ArrowUp") {
      this.quantity = this.quantity + 1;
    } else if (event.key === "ArrowDown") {
      this.quantity = this.quantity - 1;
    }
  }
}
customElements.define('qty-input', QtyInput, { extends: 'input' });

class SideDrawer extends HTMLElement {
  constructor() {
    super();
    this.overlay = document.querySelector('.js-overlay');
  }

  /**
   * Handles a 'click' event on the drawer.
   * @param {object} evt - Event object.
   */
  handleClick(evt) {
    if (evt.target.matches('.js-close-drawer') || evt.target === this.overlay) {
      this.close();
    }
  }

  /**
   * Opens the drawer.
   * @param {Element} [opener] - Element that triggered opening of the drawer.
   * @param {Element} [elementToFocus] - Element to focus after drawer opened.
   * @param {Function} [callback] - Callback function to trigger after the open has completed
   */
  open(opener, elementToFocus, callback) {
    this.dispatchEvent(new CustomEvent(`on:${this.dataset.name}:before-open`, {
      bubbles: true
    }));

    // Prevent page behind from scrolling when side drawer is open.
    this.scrollY = window.scrollY;
    document.body.classList.add('fixed');
    document.body.style.top = `-${this.scrollY}px`;
    document.documentElement.style.height = '100vh';

    this.overlay.classList.add('is-visible');
    this.setAttribute('open', '');
    this.setAttribute('aria-hidden', 'false');
    this.opener = opener;

    trapFocus(this, elementToFocus);

    // Create event handler variables (so the bound event listeners can be removed).
    this.clickHandler = this.clickHandler || this.handleClick.bind(this);
    this.keyupHandler = (evt) => {
      if (evt.key !== 'Escape' || evt.target.closest('.cart-drawer-popup')) return;
      this.close();
    };

    // Add event listeners (for while drawer is open).
    this.addEventListener('click', this.clickHandler);
    this.addEventListener('keyup', this.keyupHandler);
    this.overlay.addEventListener('click', this.clickHandler);

    // Handle events after the drawer opens
    const transitionDuration = parseFloat(getComputedStyle(this).getPropertyValue('--longest-transition-in-ms'));
    setTimeout(() => {
      if (callback) callback();
      this.dispatchEvent(new CustomEvent(`on:${this.dataset.name}:after-open`, {
        bubbles: true
      }));
    }, transitionDuration);
  }

  /**
   * Closes the drawer.
   * @param {Function} [callback] - Call back function to trigger after the close has completed
   */
  close(callback) {
    this.dispatchEvent(new CustomEvent(`on:${this.dataset.name}:before-close`, {
      bubbles: true
    }));

    this.removeAttribute('open');
    this.setAttribute('aria-hidden', 'true');
    this.overlay.classList.remove('is-visible');

    removeTrapFocus(this.opener);

    // Restore page position and scroll behaviour.
    document.documentElement.style.height = '';
    document.body.style.top = '';
    document.body.classList.remove('fixed');
    window.scrollTo(0, this.scrollY);

    // Remove event listeners added on drawer opening.
    this.removeEventListener('click', this.clickHandler);
    this.removeEventListener('keyup', this.keyupHandler);
    this.overlay.removeEventListener('click', this.clickHandler);

    // Handle events after the drawer closes
    const transitionDuration = parseFloat(getComputedStyle(this).getPropertyValue('--longest-transition-in-ms'));
    setTimeout(() => {
      if (callback) callback();
      this.dispatchEvent(new CustomEvent(`on:${this.dataset.name}:after-close`, {
        bubbles: true
      }));
    }, transitionDuration);
  }
}

customElements.define('side-drawer', SideDrawer);

window.addEventListener(
    'resize',
    debounce(() => {
      window.dispatchEvent(new CustomEvent('on:debounced-resize'));
    })
);

/**
 * Keeps a record of the height of the given child element and stores it in a css variable called
 * `--element-height`. This height is kept up to date when the browser changes size or the child
 * element mutates.
 *
 * The selector must only match one element, and don't nest watched elements.
 *
 * Example usage:
 * <slide-show data-css-var-height=".quick-nav">
 * ... will result in:
 * <slide-show data-css-var-height=".quick-nav" style="--element-height: 483px;">
 */
document.addEventListener('DOMContentLoaded', () => {
  const parentElems = document.querySelectorAll('[data-css-var-height]');
  if (parentElems) {
    const updateHeight = (elem) => {
      const parentElem = elem.closest('[data-css-var-height]');
      parentElem.style.setProperty('--element-height', `${elem.clientHeight}px`);
    };

    let mutationObserver = null;
    if ('MutationObserver' in window) {
      mutationObserver = new MutationObserver(
          debounce((mutationList) => {
            const elemToWatch = mutationList[0].target.closest('[data-css-var-height]');
            updateHeight(elemToWatch.querySelector(elemToWatch.dataset.cssVarHeight));
          })
      );
    }

    parentElems.forEach((parentElem) => {
      const elemToWatch = parentElem.querySelector(parentElem.dataset.cssVarHeight);
      if (elemToWatch) {
        if (mutationObserver) {
          mutationObserver.observe(elemToWatch, {
            childList: true,
            attributes: true,
            subtree: true
          });
        }

        window.addEventListener('on:debounced-resize', () => {
          updateHeight(elemToWatch);
        });

        updateHeight(elemToWatch);
      } else {
        // eslint-disable-next-line
        console.warn(
            `Enterprise Theme: No child elements matching ${parentElem.dataset.cssVarHeight} could be found.`
        );
      }
    });
  }
});

/**
 * Provides convenient utility functions for interacting with elements
 */
(() => {
  theme.elementUtil = {};

  /**
   * Allows for removal of elements in one line of code
   * @param {object} elem - Element to remove
   */
  theme.elementUtil.remove = (elem) => {
    if (elem) {
      if (typeof elem.remove === 'function') {
        elem.remove();
      } else {
        elem.forEach((thisElem) => {
          thisElem.remove();
        });
      }
    }
  };

  /**
   * Checks if the passed element is in viewport or not
   * @param {object} elem - Element to check the view of
   * @returns {boolean}
   */
  theme.elementUtil.isInViewport = (elem) => {
    const rect = elem.getBoundingClientRect();
    return (
        Math.round(rect.top) >= 0
        && Math.round(rect.left) >= 0
        && Math.round(rect.bottom) <= (window.innerHeight || document.documentElement.clientHeight)
        && Math.round(rect.right) <= (window.innerWidth || document.documentElement.clientWidth)
    );
  };
})();

/**
 * Utility functions for interacting with LocalStorage/SessionStorage
 */
(() => {
  theme.storageUtil = {};

  theme.storageUtil.set = (key, value, isSession) => {
    if (isSession) {
      sessionStorage.setItem(
          `cc-${key}`,
          typeof value === 'object' ? JSON.stringify(value) : value
      );
    } else {
      localStorage.setItem(`cc-${key}`, typeof value === 'object' ? JSON.stringify(value) : value);
    }
  };

  theme.storageUtil.get = (key, isJson, isSession) => {
    let value = isSession ? sessionStorage.getItem(`cc-${key}`) : localStorage.getItem(`cc-${key}`);
    if (isJson) {
      value = JSON.parse(value);
    }
    return value;
  };

  theme.storageUtil.remove = (key, isSession) => {
    if (isSession) {
      sessionStorage.removeItem(key);
    } else {
      localStorage.removeItem(key);
    }
  };
})();

class StoreHeader extends HTMLElement {
  constructor() {
    super();
    this.menu = this.querySelector('.main-menu__content');
    this.searchToggle = this.querySelector('.js-show-search');
    this.mobNavToggle = this.querySelector('.main-menu__toggle');
    this.shakeyCartIcon = this.querySelector('.header__icon--cart-shake');
    this.headerGroupSections = document.querySelectorAll('.shopify-section-group-header-group');

    this.stickyInitialised = false;
    this.stickyTransitioning = false;
    this.lastScrollPos = 0;

    this.headerTransitionSpeed = parseFloat(
        getComputedStyle(this).getPropertyValue('--header-transition-speed')
    );

    window.setHeaderHeight();
    this.bindEvents();
    this.init();
  }

  disconnectedCallback() {
    window.removeEventListener('on:debounced-resize', this.resizeHandler);
    if (this.breakpointChangeHandler) {
      window.removeEventListener('on:breakpoint-change', this.breakpointChangeHandler);
    }
  }

  bindEvents() {
    this.resizeHandler = this.resizeHandler || this.updateHeaderHeights.bind(this);
    window.addEventListener('on:debounced-resize', this.resizeHandler);

    this.mobNavToggle.addEventListener('click', window.setHeaderHeight);
    this.mobNavToggle.addEventListener('click', this.setHeaderEnd.bind(this));

    if (this.dataset.isSticky) {
      this.breakpointChangeHandler = this.breakpointChangeHandler || this.init.bind(this);
      window.addEventListener('on:breakpoint-change', this.breakpointChangeHandler);
    }

    if (this.dataset.isSearchMinimised) {
      this.searchToggle.addEventListener('click', this.handleSearchToggleClick.bind(this));
    }
  }

  /**
   * Init sticky behaviour of the header
   */
  init() {
    this.updateHeaderHeights();

    if (this.dataset.isSticky) {
      if (theme.mediaMatches.md && !this.stickyInitialised) {
        this.stickyInitialised = true;
        // Animate the menu in/out on scroll up/down
        window.addEventListener('scroll', this.handleScroll.bind(this));
      }

      setTimeout(() => {
        document.querySelector('.cc-header').classList.add('cc-header--sticky');
      });

      this.setMenuHeight();
      this.setHeaderEnd();
    }

    // Stop the cart icon from shaking when necessary
    if (this.shakeyCartIcon) {
      let pageCount = theme.storageUtil.get('shake-page-count', false, true);
      pageCount = pageCount ? parseInt(pageCount, 10) + 1 : 1;
      const shakeFrequency = parseInt(this.shakeyCartIcon.dataset.shakeFrequency, 10);
      if (pageCount < shakeFrequency) {
        this.shakeyCartIcon.classList.remove('header__icon--cart-shake');
      } else {
        pageCount = 0;
      }
      theme.storageUtil.set('shake-page-count', pageCount, true);
    }
  }

  /**
   * Toggles visibility of the search bar (mobile only)
   * @param {object} evt - Event object
   */
  handleSearchToggleClick(evt) {
    evt.preventDefault();
    const searchBar = this.querySelector('.js-search-bar');
    if (this.classList.contains('search-is-collapsed')) {
      this.classList.remove('search-is-collapsed');

      // Wait for reveal animation to complete
      setTimeout(() => {
        this.classList.add('search-is-visible');
        const searchInput = searchBar.querySelector('.js-search-input');
        searchInput.focus();
        window.setHeaderHeight();
      }, this.headerTransitionSpeed);
    } else {
      this.classList.remove('search-is-visible');

      setTimeout(() => {
        this.classList.add('search-is-collapsed');
      });

      setTimeout(window.setHeaderHeight, this.headerTransitionSpeed);
    }
  }

  /**
   * Wrapper for calling the two below
   */
  updateHeaderHeights() {
    if (theme.mediaMatches.md) {
      this.setMenuHeight();
      setTimeout(window.setHeaderHeight, this.headerTransitionSpeed);
    } else {
      window.setHeaderHeight();
    }

    // Set a css variable to record where the page content starts
    if (this.headerGroupSections && this.headerGroupSections.length > 0) {
      let headerGroupHeight = 0;
      this.headerGroupSections.forEach((section) => {
        headerGroupHeight += section.getBoundingClientRect().height;
      });

      if (headerGroupHeight > 0) {
        document.documentElement.style.setProperty(
            '--content-start',
            `${headerGroupHeight.toFixed(1)}px`
        );
      }
    }
  }

  /**
   * Set a css variable to the height of the menu links
   */
  setMenuHeight() {
    if (this.menu && this.menu.clientHeight) {
      this.style.setProperty('--menu-height', `${this.menu.clientHeight + 16}px`);
      document.documentElement.style.setProperty('--header-height', `${this.clientHeight}px`);
    }
  }

  /**
   * Handles 'scroll' event on the header
   */
  handleScroll() {
    if (!document.body.classList.contains('fixed') && !this.stickyTransitioning) {
      if (document.documentElement.scrollTop < 200) {
        this.show();
      } else if (this.lastScrollPos < document.documentElement.scrollTop) {
        this.hide();
      } else if (this.lastScrollPos - 5 > document.documentElement.scrollTop) {
        this.show();
      }

      this.lastScrollPos = document.documentElement.scrollTop;
    }
  }

  /**
   * Set a css variable to indicate where the nav ends on the page
   */
  setHeaderEnd() {
    const headerEnd = Number(this.getBoundingClientRect().top + this.clientHeight);
    document.documentElement.style.setProperty('--header-end', `${headerEnd.toFixed(1)}px`);
    document.documentElement.style.setProperty(
        '--header-end-padded',
        `${(headerEnd + (theme.mediaMatches.md ? 56 : 20)).toFixed(1)}px`
    );
  }

  /**
   * Updates hidden class for header element
   */
  show() {
    this.classList.remove('is-out');
    this.stickyTransitioning = true;
    setTimeout(() => {
      this.lastScrollPos = document.documentElement.scrollTop;
      this.stickyTransitioning = false;
      this.handleScroll();
      this.setHeaderEnd();
    }, 300);
  }

  /**
   * Updates hidden class for header element
   */
  hide() {
    if (!this.stickyTransitioning) {
      this.classList.add('is-out');
      this.stickyTransitioning = true;
      setTimeout(() => {
        this.lastScrollPos = document.documentElement.scrollTop;
        this.stickyTransitioning = false;
        this.handleScroll();
        this.setHeaderEnd();
      }, 300);
    }
  }
}

customElements.define('store-header', StoreHeader);

class MainMenu extends HTMLElement {
  constructor() {
    super();
    this.mainDisclosure = this.querySelector('.main-menu__disclosure');
    this.mainToggle = this.querySelector('.main-menu__toggle');
    this.firstLevelMenuLinks = this.querySelectorAll('.js-nav-hover');
    this.firstLevelSingleLinks = this.querySelectorAll('.main-nav__item--primary:not(.main-nav__item-content)');
    this.nav = this.querySelector('.main-nav');
    this.overlay = document.querySelector('.js-overlay');
    this.searchIcon = document.querySelector('.header__icons .js-show-search');
    this.sidebarLinks = this.querySelectorAll('.js-sidebar-hover');
    this.elementsWhichCloseMenus = document.querySelectorAll('.js-closes-menu');

    this.childNavOpen = false;
    this.overlayOpen = false;

    this.addListeners();
    this.init();
  }

  disconnectedCallback() {
    window.removeEventListener('focusin', this.focusOutHandler);
    window.removeEventListener('on:breakpoint-change', this.breakpointChangeHandler);

    if (Shopify.designMode) {
      document.removeEventListener('shopify:block:select', this.blockSelectHandler);
      document.removeEventListener('shopify:block:deselect', this.blockDeselectHandler);
    }
  }

  addListeners() {
    this.focusOutHandler = this.focusOutHandler || this.handleFocusOut.bind(this);
    this.breakpointChangeHandler = this.breakpointChangeHandler || this.init.bind(this);

    this.mainDisclosure.addEventListener('transitionend', MainMenu.handleTransition.bind(this));
    this.mainToggle.addEventListener('click', this.handleMainMenuToggle.bind(this));
    this.nav.addEventListener('click', MainMenu.handleNavClick.bind(this));
    window.addEventListener('focusin', this.focusOutHandler);
    window.addEventListener('on:breakpoint-change', this.breakpointChangeHandler);

    if (Shopify.designMode) {
      this.blockSelectHandler = this.blockSelectHandler || this.handleBlockSelect.bind(this);
      this.blockDeselectHandler = this.blockDeselectHandler || this.handleBlockDeselect.bind(this);
      document.addEventListener('shopify:block:select', this.blockSelectHandler);
      document.addEventListener('shopify:block:deselect', this.blockDeselectHandler);
    }

    if (!theme.mediaMatches.md && this.searchIcon) {
      this.searchIcon.addEventListener('click', this.closeMainDisclosure.bind(this));
    }
  }

  /**
   * Sets 'open' state of the main disclosure element.
   * @param {?object} evt - Event object.
   */
  init(evt) {
    if (!evt) {
      this.mainDisclosure.open = theme.mediaMatches.md;
    } else if (!theme.mediaMatches.md && !this.childNavOpen) {
      this.close(this.mainDisclosure);

      if (this.overlayOpen) this.toggleOverlay(false);
    } else {
      // If there's another menu open, close it.
      const activeDisclosure = this.nav.querySelector('details.is-open');
      if (activeDisclosure) {
        this.close(activeDisclosure);
      } else {
        MainMenu.open(this.mainDisclosure, false);
      }

      if (!this.childNavOpen) {
        if (this.overlayOpen) this.toggleOverlay(false);
      }
    }

    // Close the submenus (they're open for no-js)
    this.querySelectorAll('.child-nav--dropdown details[open]').forEach((childToggle) => {
      childToggle.removeAttribute('open');
    });

    if (theme.device.hasHover) {
      // Add event handler (so the bound event listener can be removed)
      this.mouseEnterMenuLinkHandler = this.mouseEnterMenuLinkHandler
          || this.handleMouseEnterMenuLink.bind(this);
      this.mouseLeaveMenuLinkHandler = this.mouseLeaveMenuLinkHandler
          || this.handleMouseLeaveMenuLink.bind(this);
      this.mouseEnterSingleLinkHandler = this.mouseEnterSingleLinkHandler
          || this.handleMouseEnterSingleLink.bind(this);
      this.mouseLeaveSingleLinkHandler = this.mouseLeaveSingleLinkHandler
          || this.handleMouseLeaveSingleLink.bind(this);
      this.mouseEnterMenuCloserHandler = this.mouseEnterMenuCloserHandler
          || this.handleClose.bind(this);

      // Bind listening events for mouse enter/leave a main menu link
      if (!this.mouseOverListening && theme.mediaMatches.md) {
        this.firstLevelMenuLinks.forEach((menuLink) => {
          menuLink.addEventListener('mouseenter', this.mouseEnterMenuLinkHandler);
          menuLink.addEventListener('mouseleave', this.mouseLeaveMenuLinkHandler);
        });
        this.firstLevelSingleLinks.forEach((singleLink) => {
          singleLink.addEventListener('mouseenter', this.mouseEnterSingleLinkHandler);
          singleLink.addEventListener('mouseleave', this.mouseLeaveSingleLinkHandler);
        });
        this.elementsWhichCloseMenus.forEach((elem) => {
          elem.addEventListener('mouseenter', this.mouseEnterMenuCloserHandler);
        });
        this.mouseOverListening = true;
      } else if (this.mouseOverListening && !theme.mediaMatches.md) {
        this.firstLevelMenuLinks.forEach((menuLink) => {
          menuLink.removeEventListener('mouseenter', this.mouseEnterMenuLinkHandler);
          menuLink.removeEventListener('mouseleave', this.mouseLeaveMenuLinkHandler);
        });
        this.firstLevelSingleLinks.forEach((singleLink) => {
          singleLink.removeEventListener('mouseenter', this.mouseEnterSingleLinkHandler);
          singleLink.removeEventListener('mouseleave', this.mouseLeaveSingleLinkHandler);
        });
        this.elementsWhichCloseMenus.forEach((elem) => {
          elem.removeEventListener('mouseenter', this.mouseEnterMenuCloserHandler);
        });
        this.mouseOverListening = false;
      }

      if (this.sidebarLinks) {
        if (!this.mouseOverSidebarListening && theme.mediaMatches.md) {
          this.sidebarLinks.forEach((sidebarLink) => {
            sidebarLink.addEventListener('mouseenter', MainMenu.handleSidenavMenuToggle);
          });
          this.mouseOverSidebarListening = true;
        } else if (this.mouseOverSidebarListening && !theme.mediaMatches.md) {
          this.sidebarLinks.forEach((sidebarLink) => {
            sidebarLink.removeEventListener('mouseenter', MainMenu.handleSidenavMenuToggle);
          });
          this.mouseOverSidebarListening = false;
        }
      }
    }
  }

  /**
   * Close the menu if the nav loses focus
   * @param {object} evt - Event object.
   */
  handleFocusOut(evt) {
    if (!this.contains(evt.target) && this.overlayOpen) this.handleClose();
  }

  /**
   * Updates the visible sidebar item
   * @param {object} evt - Event object.
   * @param {Element} [summaryElem] - The summary element to open.
   */
  static handleSidenavMenuToggle(evt, summaryElem = evt.target) {
    const container = summaryElem.closest('.child-nav');
    const lastSidenavElem = container.querySelector('.is-visible');
    if (lastSidenavElem) {
      lastSidenavElem.classList.remove('is-visible');
    }
    summaryElem.classList.add('is-visible');

    // Maintain a CSS variable which records the height of the current sidebar links
    const menu = summaryElem.closest('nav-menu');
    if (menu) {
      const openDisclosure = menu.querySelector('.disclosure__panel');
      if (openDisclosure) {
        container.style.setProperty(
            '--sidebar-height',
            `${Number.parseInt(openDisclosure.getBoundingClientRect().height, 10)}px`
        );
      }
    }
  }

  /**
   * Handles 'toggle' event on the main disclosure element.
   * @param {object} evt - Event object.
   */
  handleMainMenuToggle(evt) {
    evt.preventDefault();
    this.opener = this.mainToggle;

    if (!this.mainDisclosure.open) {
      MainMenu.open(this.mainDisclosure);
    } else {
      this.close(this.mainDisclosure, true);
    }
  }

  /**
   * Handles 'mouseenter' event on the main menu items using a timeout to infer hover intent
   * @param {object} evt - Event object
   */
  handleMouseEnterMenuLink(evt) {
    this.menuLinkTimeout = setTimeout(
        this.openMenuFromMouseEnter.bind(this, evt.target),
        Number.parseInt(this.dataset.menuSensitivity, 10)
    );
  }

  /**
   * Handles 'mouseleave' event on the main menu items - clears the timeout
   */
  handleMouseLeaveMenuLink() {
    if (this.menuLinkTimeout) clearTimeout(this.menuLinkTimeout);
  }

  /**
   * Handles 'mouseenter' event on links with no submenu items using a timeout to infer hover intent
   */
  handleMouseEnterSingleLink() {
    this.singleLinkTimeout = setTimeout(() => {
      this.handleClose();
    }, Number.parseInt(this.dataset.menuSensitivity, 10));
  }

  /**
   * Handles 'mouseleave' event on links with no submenu - clears the timeout
   */
  handleMouseLeaveSingleLink() {
    if (this.singleLinkTimeout) clearTimeout(this.singleLinkTimeout);
  }

  /**
   * Opens the menu being hovered over
   * @param {Element} menuElem - The menu element to open.
   */
  openMenuFromMouseEnter(menuElem) {
    trapFocus(menuElem);

    const disclosure = menuElem.closest('details');
    if (!disclosure.classList.contains('is-open')) {
      const activeDisclosure = this.nav.querySelector('details.is-open');

      // If there's another menu open, close it.
      if (activeDisclosure && activeDisclosure !== disclosure) {
        this.close(activeDisclosure);
      } else {
        this.toggleOverlay(!this.overlayOpen);
      }

      MainMenu.open(disclosure);
    }
  }

  /**
   * Handles 'click' event on the nav.
   * @param {object} evt - Event object.
   */
  static handleNavClick(evt) {
    const mainMenuContent = evt.target.closest('.main-menu__content');
    let el = evt.target;

    // Handle sidebar link clicks
    if (theme.mediaMatches.md && el.matches('.js-sidebar-hover')) {
      evt.preventDefault();
      MainMenu.handleSidenavMenuToggle(evt);
    }

    // Don't follow # links
    if (evt.target.href && evt.target.href.endsWith('#')) evt.preventDefault();

    // If we're on a device without hover on a larger screen, open the menu
    if (theme.mediaMatches.md && !theme.device.hasHover) {
      el = evt.target.closest('.js-toggle');
      if (!el) return;
    }

    if (!el.matches('.js-toggle,.js-back')) return;

    const disclosure = el.closest('details');

    if (theme.mediaMatches.md && theme.device.hasHover) {
      disclosure.classList.toggle('is-open');
      return;
    }

    this.opener = el;

    if (el.matches('.js-toggle')) {
      evt.preventDefault();

      if (!theme.mediaMatches.md) {
        mainMenuContent.classList.add('main-menu__content--no-focus');
      }

      if (!disclosure.classList.contains('is-open')) {
        this.childNavOpen = true;

        const activeDisclosure = this.nav.querySelector('details.is-open');
        // If there's another menu open, close it.
        if (activeDisclosure && activeDisclosure !== disclosure) {
          this.close(activeDisclosure);
        } else if (theme.mediaMatches.md) {
          this.toggleOverlay(!this.overlayOpen);
        }

        MainMenu.open(disclosure);
      } else {
        this.close(disclosure, true);
        this.childNavOpen = false;
        this.toggleOverlay(false);
      }
    } else if (el.matches('.js-back')) {
      evt.preventDefault();
      this.close(disclosure, true);
      this.childNavOpen = false;

      if (!theme.mediaMatches.md) {
        mainMenuContent.classList.remove('main-menu__content--no-focus');
      }
    }
  }

  /**
   * Handles 'transitionend' event on the nav.
   * @param {object} evt - Event object.
   */
  static handleTransition(evt) {
    const disclosure = evt.target.closest('details');

    if (disclosure.classList.contains('is-closing')) {
      disclosure.classList.remove('is-closing');
      disclosure.open = false;

      removeTrapFocus();
      this.opener = null;
    }
  }

  /**
   * Handles a 'click' event on the overlay and a 'keyup' event on the nav.
   * @param {object} evt - Event object.
   */
  handleClose(evt) {
    if (evt && evt.type === 'keyup' && evt.key !== 'Escape') return;

    const disclosure = theme.mediaMatches.md
        ? this.nav.querySelector('details.is-open')
        : this.mainDisclosure;

    if (disclosure) {
      this.close(disclosure, true);
      this.toggleOverlay(false);
      this.childNavOpen = false;
    }
  }

  /**
   * Toggles visibility of the background overlay.
   * @param {boolean} show - Show the overlay.
   */
  toggleOverlay(show) {
    this.overlayOpen = show;
    this.overlay.classList.toggle('overlay--nav', show);
    this.overlay.classList.toggle('is-visible', show);

    if (show) {
      // Add event handler (so the bound event listener can be removed).
      this.closeHandler = this.closeHandler || this.handleClose.bind(this);

      // Add event listeners (for while the nav is open).
      this.overlay.addEventListener('click', this.closeHandler);
      this.nav.addEventListener('keyup', this.closeHandler);

      if (theme.mediaMatches.md) {
        this.overlay.addEventListener('mouseenter', this.closeHandler);
      }
    } else {
      // Remove event listener added on nav opening.
      this.overlay.removeEventListener('click', this.closeHandler);
      this.nav.removeEventListener('keyup', this.closeHandler);

      if (theme.mediaMatches.md) {
        this.overlay.removeEventListener('mouseenter', this.closeHandler);
      }
    }
  }

  /**
   * Closes the main nav menu
   */
  closeMainDisclosure() {
    if (this.mainDisclosure.classList.contains('is-open')) {
      this.close(this.mainDisclosure, true);
      this.toggleOverlay(false);
      this.childNavOpen = false;
    }
  }

  /**
   * Updates open/opening classes for disclosure element.
   * @param {Element} el - Disclosure element.
   * @param {boolean} [mainMenuOpen=true] - Main menu is open.
   */
  static open(el, mainMenuOpen = true) {
    el.open = true;

    // Cap the max width of grandchildren on desktop their contents don't widen the dropdown
    if (theme.mediaMatches.md && !el.classList.contains('js-mega-nav')) {
      // If the nav menu spills off the right of the screen, shift it to the left
      const dropdownContainer = el.querySelector('.main-nav__child');
      if (dropdownContainer.getBoundingClientRect().right > window.innerWidth) {
        dropdownContainer.classList.add('main-nav__child--offset-right');
      }

      const dropdown = el.querySelector('.child-nav--dropdown');
      if (dropdown) {
        dropdown.querySelectorAll('.main-nav__grandchild').forEach((grandchildElem) => {
          grandchildElem.style.maxWidth = `${dropdown.clientWidth}px`;
        });
      }
    } else if (theme.mediaMatches.md && el.querySelector('.mega-nav--sidebar')) {
      const firstSummaryElem = el.querySelector('.mega-nav--sidebar details[open] summary');
      if (firstSummaryElem) {
        // Open the first sidebar mega menu
        MainMenu.handleSidenavMenuToggle(null, firstSummaryElem);
      }
    }

    // Slight delay required before starting transitions.
    setTimeout(() => {
      el.classList.remove('is-closing');
      el.classList.add('is-open');
    });

    if (mainMenuOpen) {
      removeTrapFocus();
      trapFocus(el);

      if (theme.mediaMatches.md || el.classList.contains('main-menu__disclosure')) {
        document.body.classList.add('overflow-hidden');
      }
    }
  }

  /**
   * Updates close/closing classes of a disclosure element.
   * @param {Element} el - Disclosure element.
   * @param {boolean} [transition=false] - Close action has a CSS transition.
   */
  close(el, transition = true) {
    el.classList.remove('is-open');

    if (transition) {
      el.classList.add('is-closing');
    } else {
      el.classList.remove('is-closing');
      el.open = false;

      removeTrapFocus(this.opener);
      this.opener = null;
    }

    setTimeout(() => {
      const offsetMenu = el.querySelector('.main-nav__child--offset-right');
      if (offsetMenu) offsetMenu.classList.remove('main-nav__child--offset-right');
    }, 200);

    if (theme.mediaMatches.md || el.classList.contains('main-menu__disclosure')) {
      document.body.classList.remove('overflow-hidden');
    }
  }

  /**
   * Decide whether to show a particular mega menu
   * @param {object} evt - Event object
   */
  handleBlockSelect(evt) {
    const activeDisclosure = this.nav.querySelector('details.is-open');
    if (activeDisclosure) {
      this.close(activeDisclosure, false);
    }

    if (evt.target.matches('.js-mega-nav')) {
      MainMenu.open(evt.target, false);
      this.toggleOverlay(true);
    }
  }

  /**
   * Decide whether to hide a particular mega menu
   * @param {object} evt - Event object
   */
  handleBlockDeselect(evt) {
    if (evt.target.matches('.js-mega-nav')) {
      this.close(evt.target, false);
      this.toggleOverlay(false);
    }
  }
}

customElements.define('main-menu', MainMenu);

class CarouselSlider extends HTMLElement {
  constructor() {
    super();
    this.slides = this.querySelectorAll('.slider__item');
    if (this.slides.length < 2) return;
    window.initLazyScript(this, this.init.bind(this));
  }

  disconnectedCallback() {
    window.removeEventListener('on:breakpoint-change', this.breakpointChangeHandler);
  }

  init() {
    this.slider = this.querySelector('.slider');
    this.grid = this.querySelector('.slider__grid');
    this.nav = this.querySelector('.slider-nav');
    this.rtl = document.dir === 'rtl';
    this.breakpointChangeHandler = this.breakpointChangeHandler
        || this.handleBreakpointChange.bind(this);

    if (this.nav) {
      this.prevBtn = this.querySelector('button[name="prev"]');
      this.nextBtn = this.querySelector('button[name="next"]');
    }

    this.initSlider();
    window.addEventListener('on:breakpoint-change', this.breakpointChangeHandler);
  }

  initSlider() {
    this.gridWidth = this.grid.clientWidth;

    // Distance between leading edges of adjacent slides (i.e. width of card + gap).
    this.slideSpan = this.getWindowOffset(this.slides[1]) - this.getWindowOffset(this.slides[0]);

    // Width of gap between slides.
    this.slideGap = this.slideSpan - this.slides[0].clientWidth;

    this.slidesPerPage = Math.round((this.gridWidth + this.slideGap) / this.slideSpan);
    this.slidesToScroll = theme.settings.sliderItemsPerNav === 'page' ? this.slidesPerPage : 1;
    this.totalPages = this.slides.length - this.slidesPerPage + 1;

    this.setCarouselState(this.totalPages > 1);
    if (this.totalPages < 2) return;

    this.sliderStart = this.getWindowOffset(this.slider);
    if (!this.sliderStart) this.sliderStart = (this.slider.clientWidth - this.gridWidth) / 2;
    this.sliderEnd = this.sliderStart + this.gridWidth;

    if (window.matchMedia('(pointer: fine)').matches) {
      this.slider.classList.add('is-grabbable');
    }

    this.addListeners();
    this.setButtonStates();
  }

  addListeners() {
    if (this.nav) {
      this.scrollHandler = debounce(this.handleScroll.bind(this));
      this.navClickHandler = this.handleNavClick.bind(this);

      this.slider.addEventListener('scroll', this.scrollHandler);
      this.nav.addEventListener('click', this.navClickHandler);
    }

    if (window.matchMedia('(pointer: fine)').matches) {
      this.mousedownHandler = this.handleMousedown.bind(this);
      this.mouseupHandler = this.handleMouseup.bind(this);
      this.mousemoveHandler = this.handleMousemove.bind(this);

      this.slider.addEventListener('mousedown', this.mousedownHandler);
      this.slider.addEventListener('mouseup', this.mouseupHandler);
      this.slider.addEventListener('mouseleave', this.mouseupHandler);
      this.slider.addEventListener('mousemove', this.mousemoveHandler);
    }
  }

  removeListeners() {
    if (this.nav) {
      this.slider.removeEventListener('scroll', this.scrollHandler);
      this.nav.removeEventListener('click', this.navClickHandler);
    }

    this.slider.removeEventListener('mousedown', this.mousedownHandler);
    this.slider.removeEventListener('mouseup', this.mouseupHandler);
    this.slider.removeEventListener('mouseleave', this.mouseupHandler);
    this.slider.removeEventListener('mousemove', this.mousemoveHandler);
  }

  /**
   * Handles 'scroll' events on the slider element.
   */
  handleScroll() {
    this.currentIndex = Math.round(this.slider.scrollLeft / this.slideSpan);
    this.setButtonStates();
  }

  /**
   * Handles 'mousedown' events on the slider element.
   * @param {object} evt - Event object.
   */
  handleMousedown(evt) {
    this.mousedown = true;
    this.startX = evt.pageX - this.sliderStart;
    this.scrollPos = this.slider.scrollLeft;
    this.slider.classList.add('is-grabbing');
  }

  /**
   * Handles 'mouseup' events on the slider element.
   */
  handleMouseup() {
    this.mousedown = false;
    this.slider.classList.remove('is-grabbing');
  }

  /**
   * Handles 'mousemove' events on the slider element.
   * @param {object} evt - Event object.
   */
  handleMousemove(evt) {
    if (!this.mousedown) return;
    evt.preventDefault();

    const x = evt.pageX - this.sliderStart;
    this.slider.scrollLeft = this.scrollPos - (x - this.startX) * 2;
  }

  /**
   * Handles 'click' events on the nav buttons container.
   * @param {object} evt - Event object.
   */
  handleNavClick(evt) {
    if (!evt.target.matches('.slider-nav__btn')) return;

    if ((evt.target.name === 'next' && !this.rtl) || (evt.target.name === 'prev' && this.rtl)) {
      this.scrollPos = this.slider.scrollLeft + this.slidesToScroll * this.slideSpan;
    } else {
      this.scrollPos = this.slider.scrollLeft - this.slidesToScroll * this.slideSpan;
    }

    this.slider.scrollTo({ left: this.scrollPos, behavior: 'smooth' });
  }

  /**
   * Handles 'on:breakpoint-change' events on the window.
   */
  handleBreakpointChange() {
    this.removeListeners();
    this.initSlider();
  }

  /**
   * Gets the offset of an element from the edge of the viewport (left for ltr, right for rtl).
   * @param {number} el - Element.
   * @returns {number}
   */
  getWindowOffset(el) {
    return this.rtl
        ? window.innerWidth - el.getBoundingClientRect().right
        : el.getBoundingClientRect().left;
  }

  /**
   * Gets the visible state of a slide.
   * @param {Element} el - Slide element.
   * @returns {boolean}
   */
  getSlideVisibility(el) {
    const slideStart = this.getWindowOffset(el);
    const slideEnd = Math.floor(slideStart + this.slides[0].clientWidth);
    return slideStart >= this.sliderStart && slideEnd <= this.sliderEnd;
  }

  /**
   * Sets the active state of the carousel.
   * @param {boolean} active - Set carousel as active.
   */
  setCarouselState(active) {
    if (active) {
      this.removeAttribute('inactive');

      // If slider width changed when activated, reinitialise it.
      if (this.gridWidth !== this.grid.clientWidth) {
        this.handleBreakpointChange();
      }
    } else {
      this.setAttribute('inactive', '');
    }
  }

  /**
   * Sets the disabled state of the nav buttons.
   */
  setButtonStates() {
    if (!this.prevBtn && !this.nextBtn) {
      return;
    }

    this.prevBtn.disabled = this.getSlideVisibility(this.slides[0]) && this.slider.scrollLeft === 0;
    this.nextBtn.disabled = this.getSlideVisibility(this.slides[this.slides.length - 1]);
  }
}

customElements.define('carousel-slider', CarouselSlider);

class ProductRecommendations extends HTMLElement {
  constructor() {
    super();
    window.initLazyScript(this, this.init.bind(this), 500);
  }

  async init() {
    const { productId } = this.dataset;
    if (!productId) return;

    try {
      const response = await fetch(`${this.dataset.url}&product_id=${productId}`);
      if (!response.ok) throw new Error(response.status);

      const tmpl = document.createElement('template');
      tmpl.innerHTML = await response.text();

      const el = tmpl.content.querySelector('product-recommendations');
      if (el && el.hasChildNodes()) {
        this.innerHTML = el.innerHTML;
      }

      window.initLazyImages();
    } catch (error) {
      console.log(error); // eslint-disable-line
    }
  }
}

customElements.define('product-recommendations', ProductRecommendations);

setTimeout(() => { requestAnimationFrame(initLazyImages); }, 0);
window.initLazyScript = initLazyScript;

document.addEventListener('keydown', (evt) => {
  if (evt.code === 'Tab') {
    document.body.classList.add('tab-used');
  }
});

document.addEventListener('DOMContentLoaded', () => {
  setTimeout(() => {
    document.body.classList.add('dom-loaded');
  }, 0);

  setTimeout(() => {
    document.body.classList.add('dom-loaded-plus-6');
  }, 6000);

  if (theme.settings.externalLinksNewTab) {
    document.addEventListener('click', (evt) => {
      const link = evt.target.tagName === 'A' ? evt.target : evt.target.closest('a');
      if (link && link.href && link.tagName === 'A' && window.location.hostname !== new URL(link.href).hostname) {
        link.target = '_blank';
      }
    });
  }

  // Ensure anchor scrolling is smooth (this shouldn't be added in the CSS)
  document.addEventListener('click', (evt) => {
    if (evt.target.href && evt.target.tagName === 'A' && window.location.hostname === new URL(evt.target.href).hostname
        && evt.target.href.includes('#')) {
      document.getElementsByTagName('html')[0].style.scrollBehavior = 'smooth';
      setTimeout(() => {
        document.getElementsByTagName('html')[0].style.scrollBehavior = '';
      }, 1000);
    }
  });
});

class SubUpdateCard extends HTMLElement {
  constructor() {
    super();
    this.KEY_LENGTH = 32;
    this.IV_BASE64_LENGTH = 24;
    this.SESSION_ID = 'dw8n8w5BkkQHMxwIC7NuX4q5ras9FRgdFVv7LEhw5dU=';
    this.popup = document.querySelector('#sub-card-popup');


    this.initCard();
    const card_number = this.querySelector('input[name="number"]');
    const card_expiry = this.querySelector('input[name="expiry"]');
    const card_cvc = this.querySelector('input[name="cvc"]');
    card_number.addEventListener('input', this.initValid.bind(this));

    card_expiry.addEventListener('input', this.initValid.bind(this));

    card_cvc.addEventListener('input', this.initValid.bind(this));

    const closeBtn = this.querySelector('button.close');
    const overlay = this.closest('#sub-card-popup').querySelector('.overlay');
    closeBtn.addEventListener('click', this.closePopup.bind(this));
    overlay.addEventListener('click', this.closePopup.bind(this));

    this.initValid();

    const form = this.querySelector('#sub-update-card-form');
    const self = this;
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      self.submitForm(this);
      return false;
    });

    this.updateTermLink();
    handleCheckoutButtonInCartPage();
  }

  updateTermLink() {
    const policyUrlMapping = {
      'en': 'https://aiavatar-help.zendesk.com/hc/en-us/articles/19910420358553-Terms-of-Use',
      'vi': 'https://aiavatar-help.zendesk.com/hc/vi/articles/19910420358553',
      'ja': 'https://aiavatar-help.zendesk.com/hc/ja/articles/19910420358553-%E5%88%A9%E7%94%A8%E8%A6%8F%E7%B4%84',
      'zh-TW': 'https://aiavatar-help.zendesk.com/hc/zh-cn/articles/19910420358553-%E5%B8%82%E5%9C%BA%E4%BD%BF%E7%94%A8%E6%9D%A1%E6%AC%BE',
      'zh-CN': 'https://aiavatar-help.zendesk.com/hc/zh-cn/articles/19910420358553-%E5%B8%82%E5%9C%BA%E4%BD%BF%E7%94%A8%E6%9D%A1%E6%AC%BE',
      'ko': 'https://aiavatar-help.zendesk.com/hc/ko/articles/19910420358553-%EC%9D%B4%EC%9A%A9-%EC%95%BD%EA%B4%80',
    };
    this.closest('#sub-card-popup').querySelector('.term').querySelector('a').setAttribute('href', policyUrlMapping[Shopify.locale]);
  }

  closePopup() {
    this.closest('#sub-card-popup').style.display = 'none';
  }

  suzShowPageLoading() {
    // loading
    const pageLoading = this.querySelector('.suzu-wrapper-loading');
    pageLoading.classList.add('suz-loading');
  }

  suzHidePageLoading() {
    // loading
    const pageLoading = this.querySelector('.suzu-wrapper-loading');
    pageLoading.classList.remove('suz-loading');
  }

  randomString(length) {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const charactersLength = characters.length;
    let result = '';
    // Create an array of 32-bit unsigned integers
    const randomValues = new Uint32Array(length);
    // Generate random values
    window.crypto.getRandomValues(randomValues);
    randomValues.forEach((value) => {
      result += characters.charAt(value % charactersLength);
    });
    return result;
  }

  encryptMessageWeb(text, sessionID) {
    const key = sessionID.slice(sessionID.length - this.KEY_LENGTH, sessionID.length);
    const iv = this.randomString(16);
    const key_utf8 = CryptoJS.enc.Utf8.parse(key);
    const iv_utf8 = CryptoJS.enc.Utf8.parse(iv);
    var cipherData = CryptoJS.AES.encrypt(text, key_utf8, { iv: iv_utf8, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 });
    const ciphertext = CryptoJS.enc.Base64.stringify(cipherData.ciphertext);
    return ciphertext + CryptoJS.enc.Base64.stringify(iv_utf8) + key + "";
  }

  validateForm() {
    const card_number = this.querySelector('input[name="number"]').value;
    const card_expiry = this.querySelector('input[name="expiry"]').value;
    const card_cvc = this.querySelector('input[name="cvc"]').value;

    if (card_number.length == 0 || card_expiry.length == 0 || card_cvc.length == 0) {
      return false;
    }
    return true;
  }

  initValid() {
    const valid = this.validateForm()
    const btnSubmit = this.querySelector('#btn-submit');
    if (!valid) {
      btnSubmit.setAttribute('disabled', 'disabled');
    } else {
      btnSubmit.removeAttribute('disabled');
    }
  }

  submitForm(form) {
    const self = this;
    this.suzShowPageLoading();
    const cardNumber = form.querySelector('input[name="number"]');
    const ccv = form.querySelector('input[name="ccv"]');
    const expiry = form.querySelector('input[name="expiry"]');
    const customerId = form.querySelector('input[name="customerId"]');
    const error = form.querySelector('.sz-sub-error');
    const responseError = form.querySelector('.sz-sub-response-error');

    responseError.classList.remove('show');
    responseError.classList.add('hidden');

    const expmm = expiry.value.split('/')[0].trimEnd();
    const expyy = expiry.value.split('/')[1].trimStart();
    const formData = new FormData(form);
    const formDataObject = Object.fromEntries(formData.entries());
    formDataObject.expyy = expyy;
    formDataObject.expmm = expmm;
    formDataObject.cardnumber = formDataObject.number.split(' ').join('');
    const requestBody = this.encryptMessageWeb(JSON.stringify(formDataObject), this.SESSION_ID);
    fetch(`${window.SHOPIFY_SUB_APP}/customer/card`, {
      method: 'POST',
      body: JSON.stringify({ data: requestBody }),
      headers: {
        'Content-Type': 'application/json',
        'Accept-Language': Shopify.locale,
        'X-Sig': window.sz_config.sub_app_key,
        'X-Epoch': window.sz_config.timestamp
      }
    }).then((response) => response.json()).then((response) => {
      if (response.error?.status === false) {
        responseError.innerText = (response.error.message == 'Invalid Cardnumber' || response.error.message == 'failure_order')
            ? responseError.dataset.invalidText
            : response.error.message;
        error.classList.remove('show');
        error.classList.add('hidden');
        responseError.classList.remove('hidden');
        responseError.classList.add('show');
        self.suzHidePageLoading();
      } else {
        if (response.data) {
          self.showPayBtns(true);
          document.querySelectorAll('.btn-sub-update-card').forEach((e) => {
            e.innerHTML = e.dataset.updateText;
          });
          const cookieName = `${customerId}-card`;
          document.cookie = `${cookieName}=${response.data}; expires=${new Date(new Date().getTime()+1000*60*60*24*365).toGMTString()}; path=/`;
        }
        self.popup.style.display = 'none';
      }
    }).catch((err) => {
      console.error(err);
      self.suzHidePageLoading();
    });
  }

  showPayBtns(flag) {
    if (!flag) {
      document.querySelectorAll('.payment-gmo').forEach((e) => {
        e.classList.add('customer-no-card');
      });
      document.querySelectorAll('#pay-manual-bank-transfer').forEach((e) => {
        e.classList.add('customer-no-card');
      });
      document.querySelectorAll('#pay-manual-bank-transfer-drawer').forEach((e) => {
        e.classList.add('customer-no-card');
      });
      document.querySelectorAll('.btn-checkout').forEach((e) => {
        e.classList.add('customer-no-card');
      });
      document.querySelectorAll('.eth-checkout-btn').forEach((e) => {
        e.classList.add('customer-no-card');
      });
      document.querySelectorAll('button[name="checkout"]').forEach((e) => {
        e.classList.add('customer-no-card');
      });
      document.querySelectorAll('.payment-web3-wallet').forEach((e) => {
        e.classList.add('customer-no-card');
      });
    } else {
      document.querySelectorAll('.payment-gmo').forEach((e) => {
        e.classList.remove('customer-no-card');
      });
      document.querySelectorAll('#pay-manual-bank-transfer').forEach((e) => {
        e.classList.add('customer-no-card');
      });
      document.querySelectorAll('#pay-manual-bank-transfer-drawer').forEach((e) => {
        e.classList.add('customer-no-card');
      });
      document.querySelectorAll('.btn-checkout').forEach((e) => {
        e.classList.remove('btn-checkout');
      });
      document.querySelectorAll('.eth-checkout-btn').forEach((e) => {
        e.classList.remove('customer-no-card');
      });
      document.querySelectorAll('button[name="checkout"]').forEach((e) => {
        e.classList.remove('customer-no-card');
      });
      document.querySelectorAll('.payment-web3-wallet').forEach((e) => {
        e.classList.remove('customer-no-card');
      });
    }
  }

  openUpdateCardPopup() {
    const popup = document.querySelector('#sub-card-popup');
    popup.querySelector('.suzu-wrapper-loading').classList.remove('suz-loading');
    popup.style.display = 'block';
  }

  initCard() {
    if (typeof Card !== 'undefined') {
      const cardForm = new Card({
        form: 'form[id="sub-update-card-form"]',
        container: '#card-wrapper-creditcard',
        formSelectors: {
          numberInput: 'form[id="sub-update-card-form"] input[name="number"]',
          expiryInput: 'form[id="sub-update-card-form"] input[name="expiry"]',
          cvcInput: 'form[id="sub-update-card-form"] input[name="cvc"]',
          nameInput: 'form[id="sub-update-card-form"] input[name="name"]'
        },
        width: 200,
        formatting: true,
        messages: {
          validDate: 'valid\ndate',
          monthYear: 'mm/yy',
        },
        placeholders: {
          number: '•••• •••• •••• ••••',
          expiry: '••/••',
          cvc: '•••'
        },
        masks: {
          cardNumber: '•'
        },
        debug: false
      });
    }
  }
}
customElements.define('sub-update-card', SubUpdateCard);

function handleCheckoutButtonInCartPage() {
  const cartForm = document.querySelector('#cart');
  let loading_bg = document.querySelector(".loading_bg");
  if (cartForm) {
    const checkoutBtn = cartForm.querySelector('button[name="checkout"].checkout-credix');
    const checkoutAccountBtn = cartForm.querySelector('button[name="checkout"].checkout-credix-account');
    var currentCurrencyRate = Shopify.currency.rate;
    if (checkoutBtn) {
      const cartTotalPrice = checkoutBtn.getAttribute('data-total');
      const maxLimitMessage = checkoutBtn.getAttribute('data-max-limit-message');
      const cartTotalInBaseCurrency = cartTotalPrice / currentCurrencyRate;
      var threshold = parseFloat(window.maximum_limit_price);
      checkoutBtn.addEventListener('click', (evt) => {
        evt.preventDefault();

        loading_bg.style.display = "block";

        purchaseCheck().then(purchase => {
          loading_bg.style.display = "none";
          if (purchase && !purchase.success) {
            // Map error code to user-friendly message
            const errorMessage = window.purchase_message && window.purchase_message[purchase.message]
                ? window.purchase_message[purchase.message]
                : purchase.message;
            showToast(errorMessage, 'error', 6000, {
              limit_value_jpy: purchase.limit_value_jpy,
              limit_duration_days: purchase.limit_duration_days
            });
            return;
          }

          inventoryCheck().then(inventory => {
            loading_bg.style.display = "none";
            if (inventory && inventory.agePriceRestriction && inventory.agePriceRestriction.blocked) {
              const reason = inventory.agePriceRestriction.reason || 'age_price_restricted';
              const ageRestrictionMessage = (window.purchase_message && window.purchase_message[reason])
                ? window.purchase_message[reason]
                : reason;
              showToast(ageRestrictionMessage, 'error', 6000);
              return;
            }
            if (inventory && inventory.purchaseTierRestriction && inventory.purchaseTierRestriction.blocked) {
              const restriction = inventory.purchaseTierRestriction;
              const tierRestrictionMessage = getPurchaseTierRestrictionMessage(restriction);
              showToast(tierRestrictionMessage, 'error', 6000);
              return;
            }
            if (inventory && inventory.outOfStockItems && inventory.outOfStockItems.length > 0) {

              const productPopup = document.querySelector('#popup-product-message');
              const itemNames = inventory.outOfStockItems.map(item =>
                  `${item.name} (SKU: ${item.sku})`
              ).join(', ');
              //productPopup.querySelector('.product_outofstock_name').innerHTML = itemNames;
              popupOpen('popup-product-message');
              return;
            }

            if (inventory && inventory.activeSubs && inventory.activeSubs.length > 0) {
              popupOpen('popup-active-subs');
              return;
            }

            if (inventory && inventory.salonBlocked) {
              setupSalonRestrictionPopup(inventory.salonGachiSku, inventory.salonGachiStatus, inventory.salonRestrictedItems);
              popupOpen('popup-salon-restriction');
              return;
            }

            if (inventory && inventory.bundleBlocked) {
              const bundleMessage = inventory.bundleBlockReason === 'bundle_incomplete'
                ? theme.strings.subscriptionBundleIncomplete
                : theme.strings.subscriptionBundleBlocked;
              showToast(bundleMessage, 'error');
              return;
            }

            if (cartTotalInBaseCurrency > threshold) {
              showToast(maxLimitMessage, 'error');
            } else {
              var authToken = getCookie(window.ENV == "DEV"? 'auth-token-test' : 'auth-token');
              if (authToken) {
                var authJson = JSON.parse(authToken);
                if(authJson.access_token) {
                  getUserInfo(authJson.access_token)
                      .then(resData => {
                        if (resData){
                          if (resData.verify_phone_status){
                            if (resData.is_agree_credix_term) {
                              const customerId = window.meta.page.customerId;
                              getCardInfo(customerId)
                                  .then(isCard => {
                                    if (isCard) {
                                      const isReceivingContractInput = document.querySelector('#is_receiving_contract');
                                      const confirmContractBtn = document.querySelector('#confirm-contract-btn');

                                      if (isReceivingContractInput && confirmContractBtn) {
                                        if (isReceivingContractInput.checked) {
                                          // Save isReceivingContract state
                                          saveIsReceivingContract(true);
                                        }
                                      }
                                      window.location = `${Shopify.routes.root}pages/checkout-credit-card`;
                                    } else {
                                      const isReceivingContractInput = document.querySelector('#is_receiving_contract');
                                      const confirmContractBtn = document.querySelector('#confirm-contract-btn');
                                      if (isReceivingContractInput && confirmContractBtn) {
                                        if (isReceivingContractInput.checked) {
                                          // Save isReceivingContract state
                                          saveIsReceivingContract(true);

                                          confirmContractBtn.setAttribute('data-payment-method', 'credix_first_purchase');
                                          const cartDrawer = document.getElementById('cart-drawer');
                                          if (cartDrawer && typeof cartDrawer.close === 'function') {
                                            cartDrawer.close();
                                          }

                                          // Load and populate sale info before opening popup
                                          const contractInfoElement = document.querySelector('contract-information');
                                          if (contractInfoElement && typeof contractInfoElement.getSaleInfo === 'function') {
                                            contractInfoElement.getSaleInfo(customerId)
                                                .then((saleInfoResult) => {
                                                  if (saleInfoResult.success && saleInfoResult.exists && saleInfoResult.data) {
                                                    if (typeof contractInfoElement.populateFormFromSaleInfo === 'function') {
                                                      contractInfoElement.populateFormFromSaleInfo(saleInfoResult.data);
                                                    }
                                                  }
                                                  // Open popup after populating form
                                                  popupOpen('popup_contract_information');
                                                })
                                                .catch((error) => {
                                                  console.error('Error loading sale info:', error);
                                                  // Still open popup even if API fails
                                                  popupOpen('popup_contract_information');
                                                });
                                          } else {
                                            // Fallback: open popup directly if contract-information element not found
                                            popupOpen('popup_contract_information');
                                          }
                                          return;
                                        } else {
                                          confirmContractBtn.removeAttribute('data-payment-method');
                                          // Save isReceivingContract state when unchecked
                                          saveIsReceivingContract(false);
                                        }
                                      }
                                      window.location = `${Shopify.routes.root}pages/3ds-checkout`;
                                    }
                                  });
                            } else {
                              popupOpen('popup-common-term-condition');
                            }
                          } else {
                            popupOpen('popup-common-verified-phone');
                          }
                        }
                      });
                }
              } else {
                window.location = `${Shopify.routes.root}pages/checkout-credit-card`;
              }
            }
          });

        });


      });
    }
    if (checkoutAccountBtn) {
      checkoutAccountBtn.addEventListener('click', (evt) => {
        evt.preventDefault();
        window.location.href = window.Shopify.routes.root + 'account';
      });
    }
  }

}

function showToast(message, type = 'success', duration = 4200, replacements = null) {
  const toastContainer = document.getElementById('toast-container');
  const toast = document.createElement('div');
  toast.classList.add('toast-message');

  // Apply different styles based on the message type
  if(type === 'success') {
    toast.classList.add('toast-success');
  } else if(type === 'error') {
    toast.classList.add('toast-error');
  }

  // Allow HTML content to support inline placeholders/spans
  toast.innerHTML = message;
  toastContainer.appendChild(toast);

  // If replacements provided, populate placeholders inside the toast
  if (replacements) {
    const { limit_duration_days, limit_value_jpy } = replacements;
    const durationEl = toast.querySelector('.limit_duration_days');
    if (durationEl && typeof limit_duration_days !== 'undefined') {
      durationEl.textContent = String(limit_duration_days);
    }
    const valueEl = toast.querySelector('.limit_value_jpy');
    if (valueEl && typeof limit_value_jpy !== 'undefined') {
      valueEl.textContent = Shopify.formatMoney(limit_value_jpy*100, "{{amount_no_decimals}}");
    }
  }

  // Show the toast
  setTimeout(() => {
    toast.style.display = 'block';
    toast.style.opacity = 1;
    toast.style.bottom = '20px'; // Move up a bit when showing
  }, 100);

  // Hide the toast after `duration`
  setTimeout(() => {
    toast.style.opacity = 0;
    toast.style.bottom = '10px'; // Move back down when hiding
    setTimeout(() => toastContainer.removeChild(toast), 500); // Remove after fade out
  }, duration);
}

var gmoOrderCheckStatusInterval = 1;
function initGmo() {
  let modalBtncart_recomend = document.getElementById("pay-gmo-recomend");
  let modalBtncart = document.getElementById("pay-gmo");
  let modalcart = document.getElementById("popup-success");
  let modalCartError = document.getElementById("popup-error");
  let closeBtncart = document.querySelector(".close-btnview");
  let closeBtncartbtn = document.querySelector(".close-btn");
  let closeBtncartclose = document.querySelector(".close-btnclose");
  let gmoCancelBtns = document.querySelectorAll('.cancel-gmo-btn');
  let loading_bg = document.querySelector(".loading_bg");
  const cartUrl = window.Shopify.routes.root + 'cart.js';
  const apiGmoCancelUrl = window.GMO_APP_URL + 'api/order/cancel';
  // Hiển thị popup khi nhấp chuột vào button
  if (modalBtncart_recomend) {
    modalBtncart_recomend.onclick = function(){
      const apiUrl = modalBtncart_recomend.getAttribute('data-api') + 'api/order/create';
      const quickAddDrawer = modalBtncart_recomend.closest('quick-add-drawer');
      const customerId = window.meta.page.customerId;
      if (!customerId) {
        window.location.href = window.Shopify.routes.root + 'account';
        return;
      }
      const quantity = quickAddDrawer.querySelector('input[name="quantity"]').value;
      const cartItems = [{
        variant_id: quickAddDrawer.querySelector('input[name="id"]').value,
        quantity: quantity
      }];
      const subProduct = quickAddDrawer.updateSubProductInput();
      if (subProduct?.variantId) {
        cartItems.push({
          variant_id: +subProduct.variantId,
          quantity: quantity
        });
      }
      loading_bg.style.display = "block";

      const requestBody = { customerId, items: cartItems };
      const affid = getCookie('affid');
      const oid = getCookie('oid');
      if (affid && oid) {
        requestBody.affInfo = {
          affId: affid,
          oid: oid
        };
      }

      fetch(apiUrl, {
        method: 'POST',
        body: JSON.stringify(requestBody),
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': Shopify.locale,
          'X-Sig': window.sz_config.gmo_key,
          'X-Epoch': window.sz_config.timestamp
        }
      })
          .then((response) => response.json())
          .then((response) => {
            if (!response.errors) {
              const account = response.account;
              const formattedAmount = new Intl.NumberFormat('ja-JP', {
                style: 'currency',
                currency: 'JPY',
              }).format(account.amountTransfer);
              modalCartError.style.display = "none";
              modalcart.style.display = "block";
              document.querySelector('#gmo_bank_code').innerHTML = account.bankCode;
              document.querySelector('#gmo_bank_name').innerHTML = account.bankName;
              document.querySelector('#gmo_branch_code').innerHTML = account.branchCode;
              document.querySelector('#gmo_account_number').innerHTML = account.accountNumber;
              document.querySelector('#gmo_branch_name_kana').innerHTML = account.branchNameKana;
              document.querySelector('#gmo_holdername_kana').innerHTML = account.holderNameKana;
              document.querySelector('#gmo_amount').innerHTML = formattedAmount;
              document.querySelector('#gmo_note').innerHTML = account.note;
              checkGmoOrderStatus(account.note);
            } else {
              modalcart.style.display = "none";
              modalCartError.style.display = "block";
              let errorMessage = Object.values(response.errors)[0];
              errorMessage = prepareCheckoutError(response, errorMessage, window.CHECKOUT_ERROR_MESSAGES.OUTSTOCK);
              modalCartError.querySelector('p').innerHTML = errorMessage;
              if (response.errors.hasPendingOrder) {
                modalCartError.querySelector('.btn-view').style.display = 'block';
              } else {
                modalCartError.querySelector('.btn-view').style.display = 'none';
              }
            }
            loading_bg.style.display = "none";
          }).catch(console.error);
    }
  }
  if (modalBtncart) {
    const apiUrl = modalBtncart.getAttribute('data-api') + 'api/order/create';
    const customerId = window.meta.page.customerId;


    var authToken = getCookie(window.ENV == "DEV"? 'auth-token-test' : 'auth-token');

    modalBtncart.onclick = function(e){
      const isReceivingContractInput = document.querySelector('#is_receiving_contract');
      const confirmContractBtn = document.querySelector('#confirm-contract-btn');
      if (isReceivingContractInput && confirmContractBtn) {
        if (isReceivingContractInput.checked) {
          // Save isReceivingContract state
          saveIsReceivingContract(true);

          confirmContractBtn.setAttribute('data-payment-method', 'gmo');
          const cartDrawer = document.getElementById('cart-drawer');
          if (cartDrawer && typeof cartDrawer.close === 'function') {
            cartDrawer.close();
          }

          // Load and populate sale info before opening popup
          const contractInfoElement = document.querySelector('contract-information');
          if (contractInfoElement && typeof contractInfoElement.getSaleInfo === 'function') {
            contractInfoElement.getSaleInfo(customerId)
                .then((saleInfoResult) => {
                  if (saleInfoResult.success && saleInfoResult.exists && saleInfoResult.data) {
                    if (typeof contractInfoElement.populateFormFromSaleInfo === 'function') {
                      contractInfoElement.populateFormFromSaleInfo(saleInfoResult.data);
                    }
                  }
                  // Open popup after populating form
                  popupOpen('popup_contract_information');
                })
                .catch((error) => {
                  console.error('Error loading sale info:', error);
                  // Still open popup even if API fails
                  popupOpen('popup_contract_information');
                });
          } else {
            // Fallback: open popup directly if contract-information element not found
            popupOpen('popup_contract_information');
          }
          return;
        } else {
          confirmContractBtn.removeAttribute('data-payment-method');
          // Save isReceivingContract state when unchecked
          saveIsReceivingContract(false);
        }
      }
      if (!customerId) {
        window.location.href = window.Shopify.routes.root + 'account';
        return;
      }
      modalBtncart.setAttribute('disabled', 'true');
      loading_bg.style.display = "block";

      purchaseCheck().then(purchase => {
        loading_bg.style.display = "none";
        if (purchase && !purchase.success) {
          // Map error code to user-friendly message
          const errorMessage = window.purchase_message && window.purchase_message[purchase.message]
              ? window.purchase_message[purchase.message]
              : purchase.message;
          showToast(errorMessage, 'error', 6000, {
            limit_value_jpy: purchase.limit_value_jpy,
            limit_duration_days: purchase.limit_duration_days
          });
          modalBtncart.removeAttribute('disabled');
          return;
        }

        inventoryCheck().then(inventory => {
          loading_bg.style.display = "none";
          if (inventory && inventory.agePriceRestriction && inventory.agePriceRestriction.blocked) {
            const reason = inventory.agePriceRestriction.reason || 'age_price_restricted';
            const ageRestrictionMessage = (window.purchase_message && window.purchase_message[reason])
              ? window.purchase_message[reason]
              : reason;
            showToast(ageRestrictionMessage, 'error', 6000);
            modalBtncart.removeAttribute('disabled');
            return;
          }
          if (inventory && inventory.purchaseTierRestriction && inventory.purchaseTierRestriction.blocked) {
            const restriction = inventory.purchaseTierRestriction;
            const tierRestrictionMessage = getPurchaseTierRestrictionMessage(restriction);
            showToast(tierRestrictionMessage, 'error', 6000);
            modalBtncart.removeAttribute('disabled');
            return;
          }
          if (inventory && inventory.outOfStockItems && inventory.outOfStockItems.length > 0) {

            const productPopup = document.querySelector('#popup-product-message');
            const itemNames = inventory.outOfStockItems.map(item =>
                `${item.name} (SKU: ${item.sku})`
            ).join(', ');
            //productPopup.querySelector('.product_outofstock_name').innerHTML = itemNames;
            popupOpen('popup-product-message');
            modalBtncart.removeAttribute('disabled');
            return;
          }

          if (inventory && inventory.activeSubs && inventory.activeSubs.length > 0) {
            popupOpen('popup-active-subs');
            modalBtncart.removeAttribute('disabled');
            return;
          }

          if (inventory && inventory.salonBlocked) {
            setupSalonRestrictionPopup(inventory.salonGachiSku, inventory.salonGachiStatus, inventory.salonRestrictedItems);
            popupOpen('popup-salon-restriction');
            modalBtncart.removeAttribute('disabled');
            return;
          }

          if (inventory && inventory.bundleBlocked) {
            const bundleMessage = inventory.bundleBlockReason === 'bundle_incomplete'
              ? theme.strings.subscriptionBundleIncomplete
              : theme.strings.subscriptionBundleBlocked;
            showToast(bundleMessage, 'error');
            modalBtncart.removeAttribute('disabled');
            return;
          }

          if (modalBtncart.classList.contains('customer-no-card')) {
            getCardInfo(customerId)
                .then(isCard => {
                  if (isCard) {
                    if (authToken) {
                      var authJson = JSON.parse(authToken);
                      if(authJson.access_token) {
                        getUserInfo(authJson.access_token)
                            .then(resData => {
                              if (resData){
                                if (resData.verify_phone_status){
                                  if (resData.is_agree_credix_term) {
                                    loading_bg.style.display = "block";
                                    getSection('cart-data')
                                        .then((data) => {
                                          const response = JSON.parse(data);
                                          if (response.items) {
                                            var itemsCart = response.items.map(function(item) {
                                              return {
                                                item_id: item.id,
                                                item_name: item.title,
                                                avatar_id: item.id,
                                                avatar_sku: item.sku,
                                                item_brand: item.avatar_type || '',
                                                price: item.price / 100,
                                                quantity: item.quantity
                                              };
                                            });
                                            if (authToken) {
                                              var authJson = JSON.parse(authToken);
                                            }
                                            trackCheckOut("GMO", authJson, itemsCart);

                                            const requestBody = { customerId, items: response.items };
                                            const affid = getCookie('affid');
                                            const oid = getCookie('oid');
                                            if (affid && oid) {
                                              requestBody.affInfo = {
                                                affId: affid,
                                                oid: oid
                                              };
                                            }

                                            fetch(apiUrl, {
                                              method: 'POST',
                                              body: JSON.stringify(requestBody),
                                              headers: {
                                                'Content-Type': 'application/json',
                                                'Accept-Language': Shopify.locale,
                                                'X-Sig': window.sz_config.gmo_key,
                                                'X-Epoch': window.sz_config.timestamp
                                              }
                                            })
                                                .then((response) => response.json())
                                                .then((response) => {
                                                  if (!response.errors) {
                                                    const account = response.account;
                                                    const formattedAmount = new Intl.NumberFormat('ja-JP', {
                                                      style: 'currency',
                                                      currency: 'JPY',
                                                    }).format(account.amountTransfer);
                                                    modalCartError.style.display = "none";
                                                    modalcart.style.display = "block";
                                                    document.querySelector('#gmo_summary_tax').innerHTML = Shopify.formatMoney(account.tax*100, "{{amount_no_decimals}}")  + ' ' + account.currency;
                                                    document.querySelector('#gmo_summary_orderamount').innerHTML =  Shopify.formatMoney(account.totalPrice*100, "{{amount_no_decimals}}")  + ' ' + account.currency;
                                                    document.querySelector('#gmo_summary_totalpayment').innerHTML =  Shopify.formatMoney((account.tax + account.totalPrice)*100, "{{amount_no_decimals}}")  + ' ' + account.currency;
                                                    document.querySelector('#gmo_bank_code').innerHTML = account.bankCode;
                                                    document.querySelector('#gmo_bank_name').innerHTML = account.bankName;
                                                    document.querySelector('#gmo_branch_code').innerHTML = account.branchCode;
                                                    document.querySelector('#gmo_account_number').innerHTML = account.accountNumber;
                                                    document.querySelector('#gmo_branch_name_kana').innerHTML = account.branchNameKana;
                                                    document.querySelector('#gmo_holdername_kana').innerHTML = account.holderNameKana;
                                                    document.querySelector('#gmo_amount').innerHTML = formattedAmount;
                                                    document.querySelector('#gmo_note').innerHTML = account.note;
                                                    clearGmoCart();
                                                    checkGmoOrderStatus(account.note);
                                                    let gmoCancelButton = document.querySelector('#confirm-cancel-gmoorder-btn');
                                                    gmoCancelButton.setAttribute('data-order-id', account.orderId);

                                                    trackPurchaseSuccess(account.orderId, authJson, itemsCart);

                                                  } else {
                                                    let errorMessage = Object.values(response.errors)[0];
                                                    let commonPopup = document.querySelector('#popup-common-message');
                                                    let messageError;
                                                    if (errorMessage === 'product.outstock') {
                                                      let productPopup = document.querySelector('#popup-product-message');
                                                      const errorDetail = Object.values(response.errors)[1] || '';
                                                      //productPopup.querySelector('.product_outofstock_name').innerHTML = errorDetail;
                                                      popupOpen('popup-product-message');
                                                      trackPurchaseFail(errorDetail);
                                                    } else {
                                                      messageError = document.querySelector('.eth-checkout-btn').getAttribute(`data-${errorMessage}`);
                                                      errorMessage = prepareCheckoutError(response, errorMessage, window.CHECKOUT_ERROR_MESSAGES.OUTSTOCK);

                                                      if (response.errors.hasPendingOrder) {
                                                        commonPopup.querySelector('.btn-view').style.display = 'block';
                                                        commonPopup.querySelector('h4').innerHTML = commonPopup.querySelector('h4').getAttribute('data-errorTitle');
                                                        commonPopup.querySelector('p').innerHTML = theme.strings.gmo.pay_pendding_text;
                                                      } else {
                                                        commonPopup.querySelector('.btn-view').style.display = 'none';
                                                        commonPopup.querySelector('h4').innerHTML = commonPopup.querySelector('h4').getAttribute('data-errorTitle');
                                                        commonPopup.querySelector('p').innerHTML = messageError ? messageError : errorMessage;
                                                      }
                                                      popupOpen('popup-common-message');

                                                      trackPurchaseFail(messageError ? messageError : errorMessage);
                                                    }

                                                  }
                                                  loading_bg.style.display = "none";
                                                  modalBtncart.removeAttribute('disabled');
                                                }).catch(console.error);
                                          } else {
                                            loading_bg.style.display = "none";
                                            modalBtncart.removeAttribute('disabled');
                                          }
                                        }).catch(console.error);
                                  } else {
                                    popupOpen('popup-common-term-condition');
                                  }
                                } else {
                                  popupOpen('popup-common-verified-phone');
                                }
                              }
                            });
                      }
                    }

                  } else {
                    popupOpen('popup-common-secure-payment');
                    return;
                  }
                });
          } else {

            if (authToken) {
              var authJson = JSON.parse(authToken);
              if(authJson.access_token) {
                getUserInfo(authJson.access_token)
                    .then(resData => {
                      if (resData){
                        if (resData.verify_phone_status){
                          loading_bg.style.display = "block";
                          getSection('cart-data')
                              .then((data) => {
                                const response = JSON.parse(data);
                                if (response.items) {
                                  var itemsCart = response.items.map(function(item) {
                                    return {
                                      item_id: item.id,
                                      item_name: item.title,
                                      avatar_id: item.id,
                                      avatar_sku: item.sku,
                                      item_brand: item.avatar_type || '',
                                      price: item.price / 100,
                                      quantity: item.quantity
                                    };
                                  });
                                  if (authToken) {
                                    var authJson = JSON.parse(authToken);
                                  }
                                  trackCheckOut("GMO", authJson, itemsCart);

                                  const requestBody = { customerId, items: response.items };
                                  const affid = getCookie('affid');
                                  const oid = getCookie('oid');
                                  if (affid && oid) {
                                    requestBody.affInfo = {
                                      affId: affid,
                                      oid: oid
                                    };
                                  }

                                  fetch(apiUrl, {
                                    method: 'POST',
                                    body: JSON.stringify(requestBody),
                                    headers: {
                                      'Content-Type': 'application/json',
                                      'Accept-Language': Shopify.locale,
                                      'X-Sig': window.sz_config.gmo_key,
                                      'X-Epoch': window.sz_config.timestamp
                                    }
                                  })
                                      .then((response) => response.json())
                                      .then((response) => {
                                        if (!response.errors) {
                                          const account = response.account;
                                          const formattedAmount = new Intl.NumberFormat('ja-JP', {
                                            style: 'currency',
                                            currency: 'JPY',
                                          }).format(account.amountTransfer);
                                          modalCartError.style.display = "none";
                                          modalcart.style.display = "block";
                                          document.querySelector('#gmo_summary_tax').innerHTML = Shopify.formatMoney(account.tax*100, "{{amount_no_decimals}}")  + ' ' + account.currency;
                                          document.querySelector('#gmo_summary_orderamount').innerHTML =  Shopify.formatMoney(account.totalPrice*100, "{{amount_no_decimals}}")  + ' ' + account.currency;
                                          document.querySelector('#gmo_summary_totalpayment').innerHTML =  Shopify.formatMoney((account.tax + account.totalPrice)*100, "{{amount_no_decimals}}")  + ' ' + account.currency;
                                          document.querySelector('#gmo_bank_code').innerHTML = account.bankCode;
                                          document.querySelector('#gmo_bank_name').innerHTML = account.bankName;
                                          document.querySelector('#gmo_branch_code').innerHTML = account.branchCode;
                                          document.querySelector('#gmo_account_number').innerHTML = account.accountNumber;
                                          document.querySelector('#gmo_branch_name_kana').innerHTML = account.branchNameKana;
                                          document.querySelector('#gmo_holdername_kana').innerHTML = account.holderNameKana;
                                          document.querySelector('#gmo_amount').innerHTML = formattedAmount;
                                          document.querySelector('#gmo_note').innerHTML = account.note;
                                          clearGmoCart();
                                          checkGmoOrderStatus(account.note);
                                          let gmoCancelButton = document.querySelector('#confirm-cancel-gmoorder-btn');
                                          gmoCancelButton.setAttribute('data-order-id', account.orderId);
                                          trackPurchaseSuccess(account.orderId, authJson, itemsCart);

                                        } else {
                                          let errorMessage = Object.values(response.errors)[0];
                                          let commonPopup = document.querySelector('#popup-common-message');
                                          let messageError;
                                          if (errorMessage === 'product.outstock') {
                                            let productPopup = document.querySelector('#popup-product-message');
                                            const errorDetail = Object.values(response.errors)[1] || '';
                                            //productPopup.querySelector('.product_outofstock_name').innerHTML = errorDetail;
                                            popupOpen('popup-product-message');
                                            trackPurchaseFail(errorDetail);
                                          } else {
                                            messageError = document.querySelector('.eth-checkout-btn').getAttribute(`data-${errorMessage}`);
                                            errorMessage = prepareCheckoutError(response, errorMessage, window.CHECKOUT_ERROR_MESSAGES.OUTSTOCK);

                                            if (response.errors.hasPendingOrder) {
                                              commonPopup.querySelector('.btn-view').style.display = 'block';
                                              commonPopup.querySelector('h4').innerHTML = commonPopup.querySelector('h4').getAttribute('data-errorTitle');
                                              commonPopup.querySelector('p').innerHTML = theme.strings.gmo.pay_pendding_text;
                                            } else {
                                              commonPopup.querySelector('.btn-view').style.display = 'none';
                                              commonPopup.querySelector('h4').innerHTML = commonPopup.querySelector('h4').getAttribute('data-errorTitle');
                                              commonPopup.querySelector('p').innerHTML = messageError ? messageError : errorMessage;
                                            }
                                            popupOpen('popup-common-message');

                                            trackPurchaseFail(messageError ? messageError : errorMessage);
                                          }

                                        }
                                        loading_bg.style.display = "none";
                                        modalBtncart.removeAttribute('disabled');
                                      }).catch(console.error);
                                } else {
                                  loading_bg.style.display = "none";
                                  modalBtncart.removeAttribute('disabled');
                                }
                              }).catch(console.error);

                        } else {
                          popupOpen('popup-common-verified-phone');
                        }
                      }

                    });
              }
            }

          }
        });

      });

    }
  }


  if (gmoCancelBtns.length) {
    gmoCancelBtns.forEach((btn) => {
      if (!btn.getAttribute('hasEvent')) {
        btn.setAttribute('hasEvent', true);
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          popupClose('popup-success');
          popupOpen('popup_gmo_cancel_order');
        })
      }
    });
  }

  document.querySelector('#confirm-cancel-gmoorder-btn').addEventListener('click', (e) => {
    e.preventDefault();
    popupClose('popup_gmo_cancel_order');
    let cancelOrderId = document.querySelector('#confirm-cancel-gmoorder-btn').getAttribute('data-order-id');
    cancelGmoOrder(cancelOrderId)
  });

  function cancelGmoOrder(orderId) {
    const customerId = window.meta.page.customerId;
    loading_bg.style.display = "block";
    let commonPopup = document.querySelector('#popup-common-message');
    fetch(apiGmoCancelUrl, {
      method: 'POST',
      body: JSON.stringify({ customerId, spOrderId: orderId }),
      headers: {
        'Content-Type': 'application/json',
        'Accept-Language': Shopify.locale,
        'X-Sig': window.sz_config.gmo_key,
        'X-Epoch': window.sz_config.timestamp
      }
    })
        .then((response) => response.json())
        .then((response) => {
          if (response.message) {
            popupOpen('popup-common-message');
            commonPopup.querySelector('h4').innerHTML = commonPopup.querySelector('h4').getAttribute('data-successTitle');
            commonPopup.querySelector('p').innerHTML = response.message;
            clearInterval(gmoOrderCheckStatusInterval);
          }
          loading_bg.style.display = "none";
        });
  }



  function checkGmoOrderStatus(orderId) {
    gmoOrderCheckStatusInterval = setInterval(() => {
      fetch(window.GMO_APP_URL + `api/order/status-check/${orderId}?customerId=${window.meta.page.customerId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': Shopify.locale,
          'X-Sig': window.sz_config.gmo_key,
          'X-Epoch': window.sz_config.timestamp
        }
      })
          .then((response) => response.json())
          .then((response) => {
            if(response.orderStatus && parseInt(response.orderStatus) > 0) {
              //window.location.href = window.Shopify.routes.root + 'account';

              popupClose('popup-success');
              document.querySelector('#gmo_order_number').innerHTML = response.orderNumber;
              document.querySelector('#gmo_success_tax').innerHTML = Shopify.formatMoney(response.tax*100, "{{amount_no_decimals}}")  + ' ' + response.currency;
              document.querySelector('#gmo_success_amount').innerHTML =  Shopify.formatMoney(response.totalPrice*100, "{{amount_no_decimals}}")  + ' ' + response.currency;
              document.querySelector('#gmo_success_date').innerHTML = response.orderDate;
              popupOpen('popup-success-gmo');
              clearInterval(gmoOrderCheckStatusInterval);
            } else if (response.orderStatus && (response.orderStatus === 9)){
              window.location.href = window.Shopify.routes.root + 'account';
            }
          });
    }, 5000);
  }
  function clearGmoCart() {
    fetch(window.Shopify.routes.root + 'cart/clear.js', {
      method: 'POST',
    })
        .then((response) => response.json())
        .then((response) => {
          if(response.item_count === 0) {
            document.dispatchEvent(new CustomEvent("dispatch:cart-drawer:refresh"));
          }
        });
  }
  if (closeBtncart) {
    closeBtncart.onclick = function(){
      modalcart.style.display = "none";
    }
  }
  if (closeBtncartbtn) {
    closeBtncartbtn.onclick = function(){
      modalCartError.style.display = "none";
    }
  }
  if (closeBtncartclose) {
    closeBtncartclose.onclick = function(){
      modalcart.style.display = "none";
    }
  }

  // Đóng khi nhấp chuột vào bất cứ khu vực nào trên màn hình
  window.onclick = function(e){
    if(e.target == modalcart){
      modalcart.style.display = "none"
    }
  }
}
initGmo();

var expireTimeInterval = 1;
var ethOrderCheckStatusInterval = 1;
var quickAddEth = false;
var ethOrderId = null;
function initEth() {
  let ethCheckoutBtns = document.querySelectorAll(".eth-checkout-btn");
  let ethJpCheckoutBtn = document.querySelector('#eth-jp-checkout-btn');
  let ethCancelBtns = document.querySelectorAll('.cancel-eth-btn');
  let loading_bg = document.querySelector(".loading_bg");
  let commonPopup = document.querySelector('#popup-common-message');
  const apiEthUrl = window.ETH_APP_URL + 'api/order/create';
  const apiEthCancelUrl = window.ETH_APP_URL + 'api/order/cancel';
  const cartUrl = window.Shopify.routes.root + 'cart.js';
  var authToken = getCookie(window.ENV == "DEV"? 'auth-token-test' : 'auth-token');

  if (ethCheckoutBtns.length) {
    ethCheckoutBtns.forEach((btn) => {

      if (!btn.getAttribute('hasEvent')) {
        btn.setAttribute('hasEvent', true);
        btn.addEventListener('click', (e) => {
          e.preventDefault();

          const customerId = window.meta.page.customerId;
          if (!customerId) {
            window.location.href = window.Shopify.routes.root + 'account';
            return;
          }

          const isReceivingContractInput = document.querySelector('#is_receiving_contract');
          const confirmContractBtn = document.querySelector('#confirm-contract-btn');
          if (isReceivingContractInput && confirmContractBtn) {
            if (isReceivingContractInput.checked) {
              // Save isReceivingContract state
              saveIsReceivingContract(true);

              confirmContractBtn.setAttribute('data-payment-method', 'eth');
              const cartDrawer = document.getElementById('cart-drawer');
              if (cartDrawer && typeof cartDrawer.close === 'function') {
                cartDrawer.close();
              }

              // Load and populate sale info before opening popup
              const contractInfoElement = document.querySelector('contract-information');
              if (contractInfoElement && typeof contractInfoElement.getSaleInfo === 'function') {
                contractInfoElement.getSaleInfo(customerId)
                    .then((saleInfoResult) => {
                      if (saleInfoResult.success && saleInfoResult.exists && saleInfoResult.data) {
                        if (typeof contractInfoElement.populateFormFromSaleInfo === 'function') {
                          contractInfoElement.populateFormFromSaleInfo(saleInfoResult.data);
                        }
                      }
                      // Open popup after populating form
                      popupOpen('popup_contract_information');
                    })
                    .catch((error) => {
                      console.error('Error loading sale info:', error);
                      // Still open popup even if API fails
                      popupOpen('popup_contract_information');
                    });
              } else {
                // Fallback: open popup directly if contract-information element not found
                popupOpen('popup_contract_information');
              }
              return;
            } else {
              confirmContractBtn.removeAttribute('data-payment-method');
              // Save isReceivingContract state when unchecked
              saveIsReceivingContract(false);
            }
          }

          loading_bg.style.display = "block";

          btn.setAttribute('disabled', 'true');

          purchaseCheck().then(purchase => {
            loading_bg.style.display = "none";
            if (purchase && !purchase.success) {
              // Map error code to user-friendly message
              const errorMessage = window.purchase_message && window.purchase_message[purchase.message]
                  ? window.purchase_message[purchase.message]
                  : purchase.message;
              showToast(errorMessage, 'error', 6000, {
                limit_value_jpy: purchase.limit_value_jpy,
                limit_duration_days: purchase.limit_duration_days
              });
              btn.removeAttribute('disabled');
              return;
            }

            inventoryCheck().then(inventory => {
              loading_bg.style.display = "none";
              if (inventory && inventory.agePriceRestriction && inventory.agePriceRestriction.blocked) {
                const reason = inventory.agePriceRestriction.reason || 'age_price_restricted';
                const ageRestrictionMessage = (window.purchase_message && window.purchase_message[reason])
                  ? window.purchase_message[reason]
                  : reason;
                showToast(ageRestrictionMessage, 'error', 6000);
                btn.removeAttribute('disabled');
                return;
              }
              if (inventory && inventory.purchaseTierRestriction && inventory.purchaseTierRestriction.blocked) {
                const restriction = inventory.purchaseTierRestriction;
                const tierRestrictionMessage = getPurchaseTierRestrictionMessage(restriction);
                showToast(tierRestrictionMessage, 'error', 6000);
                btn.removeAttribute('disabled');
                return;
              }
              if (inventory && inventory.outOfStockItems && inventory.outOfStockItems.length > 0) {

                const productPopup = document.querySelector('#popup-product-message');
                const itemNames = inventory.outOfStockItems.map(item =>
                    `${item.name} (SKU: ${item.sku})`
                ).join(', ');
                //productPopup.querySelector('.product_outofstock_name').innerHTML = itemNames;
                popupOpen('popup-product-message');
                btn.removeAttribute('disabled');
                return;
              }

              if (inventory && inventory.activeSubs && inventory.activeSubs.length > 0) {
                popupOpen('popup-active-subs');
                btn.removeAttribute('disabled');
                return;
              }

              if (inventory && inventory.salonBlocked) {
                setupSalonRestrictionPopup(inventory.salonGachiSku, inventory.salonGachiStatus, inventory.salonRestrictedItems);
                popupOpen('popup-salon-restriction');
                btn.removeAttribute('disabled');
                return;
              }

              if (inventory && inventory.bundleBlocked) {
                const bundleMessage = inventory.bundleBlockReason === 'bundle_incomplete'
                  ? theme.strings.subscriptionBundleIncomplete
                  : theme.strings.subscriptionBundleBlocked;
                showToast(bundleMessage, 'error');
                btn.removeAttribute('disabled');
                return;
              }

              if (e.target.classList.contains('customer-no-card')) {
                getCardInfo(customerId)
                    .then(isCard => {
                      if (isCard) {
                        var authToken = getCookie(window.ENV == "DEV"? 'auth-token-test' : 'auth-token');
                        if (authToken) {
                          var authJson = JSON.parse(authToken);
                          if(authJson.access_token) {
                            getUserInfo(authJson.access_token)
                                .then(resData => {
                                  if (resData){
                                    if (resData.verify_phone_status){
                                      if (resData.is_agree_credix_term) {
                                        const inQuickAdd = btn.closest('.quick-add__footer') ? true : false;
                                        if (document.querySelector('#received-address').value) {
                                          popupOpen('popup_jp_received_address');
                                          quickAddEth = inQuickAdd ? btn : false;
                                        } else {
                                          if (inQuickAdd) {
                                            createQuickEthOrder(btn);
                                            quickAddEth = btn;
                                          } else {
                                            createEthOrder();
                                            quickAddEth = false;
                                          }
                                        }
                                      } else {
                                        popupOpen('popup-common-term-condition');
                                      }
                                    } else {
                                      popupOpen('popup-common-verified-phone');
                                    }
                                  }
                                });
                          }
                        }

                      } else {
                        popupOpen('popup-common-secure-payment');
                        return;
                      }
                      btn.removeAttribute('disabled');
                    });
              } else {
                const inQuickAdd = btn.closest('.quick-add__footer') ? true : false;
                if (document.querySelector('#received-address').value) {
                  popupOpen('popup_jp_received_address');
                  quickAddEth = inQuickAdd ? btn : false;
                } else {
                  if (inQuickAdd) {
                    createQuickEthOrder(btn);
                    quickAddEth = btn;
                  } else {
                    createEthOrder();
                    quickAddEth = false;
                  }
                }
                btn.removeAttribute('disabled');
              }

            });

          });



          return;
        });
      }
    });
  }

  if (ethJpCheckoutBtn) {
    if (!ethJpCheckoutBtn.getAttribute('hasEvent')) {
      ethJpCheckoutBtn.setAttribute('hasEvent', true);
      ethJpCheckoutBtn.addEventListener('click', (e) => {
        e.preventDefault();
        ethJpCheckoutBtn.setAttribute('disabled', 'true');
        const customerId = window.meta.page.customerId;
        loading_bg.style.display = "block";

        const isReceivingContractInput = document.querySelector('#is_receiving_contract');
        const confirmContractBtn = document.querySelector('#confirm-contract-btn');
        if (isReceivingContractInput && confirmContractBtn) {
          if (isReceivingContractInput.checked) {
            // Save isReceivingContract state
            saveIsReceivingContract(true);

            confirmContractBtn.setAttribute('data-payment-method', 'eth');
            const cartDrawer = document.getElementById('cart-drawer');
            if (cartDrawer && typeof cartDrawer.close === 'function') {
              cartDrawer.close();
            }

            // Load and populate sale info before opening popup
            const contractInfoElement = document.querySelector('contract-information');
            if (contractInfoElement && typeof contractInfoElement.getSaleInfo === 'function') {
              contractInfoElement.getSaleInfo(customerId)
                  .then((saleInfoResult) => {
                    if (saleInfoResult.success && saleInfoResult.exists && saleInfoResult.data) {
                      if (typeof contractInfoElement.populateFormFromSaleInfo === 'function') {
                        contractInfoElement.populateFormFromSaleInfo(saleInfoResult.data);
                      }
                    }
                    // Open popup after populating form
                    popupOpen('popup_contract_information');
                  })
                  .catch((error) => {
                    console.error('Error loading sale info:', error);
                    // Still open popup even if API fails
                    popupOpen('popup_contract_information');
                  });
            } else {
              // Fallback: open popup directly if contract-information element not found
              popupOpen('popup_contract_information');
            }
            return;
          } else {
            confirmContractBtn.removeAttribute('data-payment-method');
            // Save isReceivingContract state when unchecked
            saveIsReceivingContract(false);
          }
        }

        purchaseCheck().then(purchase => {
          loading_bg.style.display = "none";
          if (purchase && !purchase.success) {
            // Map error code to user-friendly message
            const errorMessage = window.purchase_message && window.purchase_message[purchase.message]
                ? window.purchase_message[purchase.message]
                : purchase.message;
            showToast(errorMessage, 'error', 6000, {
              limit_value_jpy: purchase.limit_value_jpy,
              limit_duration_days: purchase.limit_duration_days
            });
            btn.removeAttribute('disabled');
            return;
          }

          inventoryCheck().then(inventory => {
            loading_bg.style.display = "none";
            if (inventory && inventory.agePriceRestriction && inventory.agePriceRestriction.blocked) {
              const reason = inventory.agePriceRestriction.reason || 'age_price_restricted';
              const ageRestrictionMessage = (window.purchase_message && window.purchase_message[reason])
                ? window.purchase_message[reason]
                : reason;
              showToast(ageRestrictionMessage, 'error', 6000);
              btn.removeAttribute('disabled');
              return;
            }
            if (inventory && inventory.purchaseTierRestriction && inventory.purchaseTierRestriction.blocked) {
              const restriction = inventory.purchaseTierRestriction;
              const tierRestrictionMessage = getPurchaseTierRestrictionMessage(restriction);
              showToast(tierRestrictionMessage, 'error', 6000);
              btn.removeAttribute('disabled');
              return;
            }
            if (inventory && inventory.outOfStockItems && inventory.outOfStockItems.length > 0) {

              const productPopup = document.querySelector('#popup-product-message');
              const itemNames = inventory.outOfStockItems.map(item =>
                  `${item.name} (SKU: ${item.sku})`
              ).join(', ');
              //productPopup.querySelector('.product_outofstock_name').innerHTML = itemNames;
              popupOpen('popup-product-message');
              btn.removeAttribute('disabled');
              return;
            }

            if (inventory && inventory.activeSubs && inventory.activeSubs.length > 0) {
              popupOpen('popup-active-subs');
              btn.removeAttribute('disabled');
              return;
            }

            if (inventory && inventory.salonBlocked) {
              setupSalonRestrictionPopup(inventory.salonGachiSku, inventory.salonGachiStatus, inventory.salonRestrictedItems);
              popupOpen('popup-salon-restriction');
              btn.removeAttribute('disabled');
              return;
            }

            if (inventory && inventory.bundleBlocked) {
              const bundleMessage = inventory.bundleBlockReason === 'bundle_incomplete'
                ? theme.strings.subscriptionBundleIncomplete
                : theme.strings.subscriptionBundleBlocked;
              showToast(bundleMessage, 'error');
              btn.removeAttribute('disabled');
              return;
            }

            if (e.target.classList.contains('customer-no-card')) {
              getCardInfo(customerId)
                  .then(isCard => {
                    if (isCard) {
                      var authToken = getCookie(window.ENV == "DEV"? 'auth-token-test' : 'auth-token');
                      if (authToken) {
                        var authJson = JSON.parse(authToken);
                        if(authJson.access_token) {
                          getUserInfo(authJson.access_token)
                              .then(resData => {
                                if (resData){
                                  if (resData.verify_phone_status){
                                    if (resData.is_agree_credix_term) {
                                      const receivedAddress = document.querySelector('#received-address').value;
                                      const receivedAddressErr = document.querySelector('#received-address-error');
                                      const receivedAddressValidationErr = document.querySelector('#received-address-validation-error');
                                      if (receivedAddress) {
                                        receivedAddressErr.style.display = 'none';
                                        var valid = WAValidator.validate(receivedAddress, 'eth');
                                        if (valid) {
                                          receivedAddressValidationErr.style.display = 'none';
                                          if (quickAddEth) {
                                            createQuickEthOrder(quickAddEth, receivedAddress);
                                          } else {
                                            createEthOrder(receivedAddress);
                                          }
                                        } else {
                                          receivedAddressValidationErr.style.display = 'block';
                                        }
                                      } else {
                                        receivedAddressErr.style.display = 'block';
                                      }
                                    } else {
                                      popupOpen('popup-common-term-condition');
                                    }
                                  } else {
                                    popupOpen('popup-common-verified-phone');
                                  }
                                }
                              });
                        }
                      }

                    } else {
                      popupOpen('popup-common-secure-payment');
                      return;
                    }
                    ethJpCheckoutBtn.removeAttribute('disabled');
                  });
            } else {
              const receivedAddress = document.querySelector('#received-address').value;
              const receivedAddressErr = document.querySelector('#received-address-error');
              const receivedAddressValidationErr = document.querySelector('#received-address-validation-error');
              if (receivedAddress) {
                receivedAddressErr.style.display = 'none';
                var valid = WAValidator.validate(receivedAddress, 'eth');
                if (valid) {
                  receivedAddressValidationErr.style.display = 'none';
                  if (quickAddEth) {
                    createQuickEthOrder(quickAddEth, receivedAddress);
                  } else {
                    createEthOrder(receivedAddress);
                  }
                } else {
                  receivedAddressValidationErr.style.display = 'block';
                }
              } else {
                receivedAddressErr.style.display = 'block';
              }
              ethJpCheckoutBtn.removeAttribute('disabled');
            }

          });

        });




      })
    }
  }

  if (ethCancelBtns.length) {
    ethCancelBtns.forEach((btn) => {
      if (!btn.getAttribute('hasEvent')) {
        btn.setAttribute('hasEvent', true);
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          btn.closest('.popup-resume').style.display = 'none';
          if (ethOrderId) {
            let ethCancelButton = document.querySelector('#confirm-cancel-ethorder-btn');
            ethCancelButton.setAttribute('data-order-id', ethOrderId);
            popupOpen('popup_eth_cancel_order');
          }
        })
      }
    });
  }

  document.querySelector('#confirm-cancel-ethorder-btn').addEventListener('click', (e) => {
    e.preventDefault();
    popupClose('popup_eth_cancel_order');
    let cancelOrderId = document.querySelector('#confirm-cancel-ethorder-btn').getAttribute('data-order-id');
    cancelEthOrder(cancelOrderId)
  });

  function cancelEthOrder(ethOrderId) {
    const customerId = window.meta.page.customerId;
    loading_bg.style.display = "block";
    fetch(apiEthCancelUrl, {
      method: 'POST',
      body: JSON.stringify({ customerId, spOrderId: ethOrderId }),
      headers: {
        'Content-Type': 'application/json',
        'Accept-Language': Shopify.locale,
        'X-Sig': window.sz_config.eth_key,
        'X-Epoch': window.sz_config.timestamp
      }
    })
        .then((response) => response.json())
        .then((response) => {
          if (response.message) {
            popupOpen('popup-common-message');
            commonPopup.querySelector('h4').innerHTML = commonPopup.querySelector('h4').getAttribute('data-successTitle');
            commonPopup.querySelector('p').innerHTML = response.message;
            clearInterval(ethOrderCheckStatusInterval);
          }
          loading_bg.style.display = "none";
        });
  }


  function createQuickEthOrder(btn, receivedAddress = null) {
    const customerId = window.meta.page.customerId;
    if (!customerId) {
      window.location.href = window.Shopify.routes.root + 'account';
      return;
    }
    const pForm = btn.closest('.quick-add__footer').querySelector('product-form');
    const quantity = pForm.querySelector('input[name="quantity"]').value;
    const pImage = btn.getAttribute('data-image') ? 'https:' + btn.getAttribute('data-image') : null;
    const cartItems = [{
      variant_id: parseInt(pForm.querySelector('input[name="id"]').value),
      quantity: quantity,
      product_id: parseInt(pForm.querySelector('input[name="product-id"]').value),
      image: pImage
    }];
    const quickAddContainer = btn.closest('quick-add-drawer');
    if (quickAddContainer) {
      const subProduct = quickAddContainer.updateSubProductInput();
      if (subProduct?.variantId) {
        cartItems.push({
          variant_id: +subProduct.variantId,
          quantity: quantity,
          product_id: +subProduct.id,
          image: null
        });
      }
    }
    loading_bg.style.display = "block";

    const requestBody = { customerId, items: cartItems, receivedAddress };
    const affid = getCookie('affid');
    const oid = getCookie('oid');
    if (affid && oid) {
      requestBody.affInfo = { affId: affid, oid: oid };
    }

    fetch(apiEthUrl, {
      method: 'POST',
      body: JSON.stringify(requestBody),
      headers: {
        'Content-Type': 'application/json',
        'Accept-Language': Shopify.locale,
        'X-Sig': window.sz_config.eth_key,
        'X-Epoch': window.sz_config.timestamp
      }
    })
        .then((response) => response.json())
        .then((resData) => {
          if (!resData.errors) {
            if (resData.data) { //create order success for both jp or non-jp
              if (!resData.data.isJpPolicy) { // for non jp
                popupOpen('popup_non_jp_order_success');
                const qrEl = document.getElementById("eth_nonjp_qrcode");
                qrEl.innerHTML = '';
                const qr = new QRCode(qrEl, {
                  text: resData.data.paymentAddress,
                  width: 200,
                  height: 200,
                });

                setTimeout(() => {
                  const qrImage = document.querySelector('#eth_nonjp_qrcode img');
                  if (qrImage) {
                    const downloadBtn = document.getElementById('download-eth-nonjp-qrcode-btn');
                    downloadBtn.style.display = 'block';
                    downloadBtn.onclick = function() {
                      downloadQRCode(qrImage.src);
                    };
                  } else {
                    downloadBtn.style.display = 'none';
                  }
                }, 500);

                expireInCountDown(resData.data.expireIn);
                document.querySelector('#eth_nonjp_amount').innerHTML = resData.data.amount + ' ETH';
                document.querySelector('#eth_nonjp_summary_estimated_payment').innerHTML = resData.data.ethAmount + ' ETH';
                document.querySelector('#eth_nonjp_account_wallet').innerHTML = resData.data.balance + ' ETH';
                document.querySelector('#eth_nonjp_amount_deposited').innerHTML = resData.data.depositAmount + ' ETH';
                document.querySelector('#eth_nonjp_summary_tax').innerHTML = Shopify.formatMoney(resData.data.tax*100, "{{amount_no_decimals}}")  + ' ' + resData.data.currency;
                document.querySelector('#eth_nonjp_summary_orderamount').innerHTML =  Shopify.formatMoney(resData.data.amount*100, "{{amount_no_decimals}}")  + ' ' + resData.data.currency;

                document.querySelector('#eth_nonjp_tranfer_address').innerHTML = resData.data.paymentAddress;
                const conversionRate = Shopify.formatMoney(resData.data.convertRate*100, "{{amount_no_decimals}}")  + ' ' + resData.data.currency;
                document.querySelector('#eth_nonjp_summary_conversion_rate').innerText = `1 ETH ~ ${conversionRate}`;
              } else {
                popupOpen('popup_jp_order_success');
                popupClose('popup_jp_received_address');
                const qrEl = document.getElementById("eth_qrcode");
                qrEl.innerHTML = '';
                const qr = new QRCode(qrEl, {
                  text: resData.data.paymentAddress,
                  width: 200,
                  height: 200,
                });

                setTimeout(() => {
                  const qrImage = document.querySelector('#eth_qrcode img');
                  if (qrImage) {
                    const downloadBtn = document.getElementById('download-eth-qrcode-btn');
                    downloadBtn.style.display = 'block';
                    downloadBtn.onclick = function() {
                      downloadQRCode(qrImage.src);
                    };
                  } else {
                    downloadBtn.style.display = 'none';
                  }
                }, 500);

                expireInCountDown(resData.data.expireIn, true);
                document.querySelector('#eth_amount_order').innerHTML = resData.data.ethAmount + ' ETH';
                document.querySelector('#eth_summary_estimated_payment').innerHTML = resData.data.ethAmount + ' ETH';
                document.querySelector('#eth_summary_tax').innerHTML = Shopify.formatMoney(resData.data.tax*100, "{{amount_no_decimals}}")  + ' ' + resData.data.currency;
                document.querySelector('#eth_summary_orderamount').innerHTML =  Shopify.formatMoney(resData.data.amount*100, "{{amount_no_decimals}}")  + ' ' + resData.data.currency;

                document.querySelector('#eth_tranfer_address').innerHTML = resData.data.paymentAddress;
                const conversionRate = Shopify.formatMoney(resData.data.convertRate*100, "{{amount_no_decimals}}")  + ' ' + resData.data.currency;
                document.querySelector('#eth_summary_conversion_rate').innerText = `1 ETH ~ ${conversionRate}`;
              }
              ethOrderId = resData.data.orderId;
              checkEthOrderStatus(resData.data.orderId);
              loading_bg.style.display = "none";
            } else {
              if (resData.isJpPolicy) { // jp need update received address
                popupOpen('popup_jp_received_address');
                if (resData.receivedAddress) {
                  document.querySelector('#received-address').value = resData.receivedAddress;
                }
                loading_bg.style.display = "none";
              }
            }
          } else {
            if (resData.errors.hasPending) {
              popupOpen('popup-has-pending');
              document.querySelector('#popup-has-pending').querySelector('p').innerHTML = resData.errors.hasPending;
            } else {
              let errorMessage = Object.values(resData.errors)[0];
              errorMessage = prepareCheckoutError(resData, errorMessage, window.CHECKOUT_ERROR_MESSAGES.OUTSTOCK);
              popupOpen('popup-common-message');
              commonPopup.querySelector('h4').innerHTML = commonPopup.querySelector('h4').getAttribute('data-errorTitle');
              commonPopup.querySelector('p').innerHTML = errorMessage;
            }
            popupClose('popup_jp_received_address');
            loading_bg.style.display = "none";
          }
        }).catch(console.error);
  }

  function downloadQRCode(qrSrc) {
    const a = document.createElement('a');
    a.href = qrSrc;
    a.download = 'qr_code.png';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  function createEthOrder(receivedAddress = null) {
    const customerId = window.meta.page.customerId;
    if (!customerId) {
      window.location.href = window.Shopify.routes.root + 'account';
      return;
    }
    loading_bg.style.display = "block";
    getSection('cart-data')
        .then((data) => {
          const response = JSON.parse(data);
          if (response.items) {

            var itemsCart = response.items.map(function(item) {
              return {
                item_id: item.id,
                item_name: item.title,
                avatar_id: item.id,
                avatar_sku: item.sku,
                item_brand: item.avatar_type || '',
                price: item.price / 100,
                quantity: item.quantity
              };
            });
            var authToken = getCookie(window.ENV == "DEV"? 'auth-token-test' : 'auth-token');
            if (authToken) {
              var authJson = JSON.parse(authToken);
            }
            trackCheckOut("ETH", authJson, itemsCart);

            const requestBody = { customerId, items: response.items, receivedAddress };
            const affid = getCookie('affid');
            const oid = getCookie('oid');
            if (affid && oid) {
              requestBody.affInfo = { affId: affid, oid: oid };
            }

            fetch(apiEthUrl, {
              method: 'POST',
              body: JSON.stringify(requestBody),
              headers: {
                'Content-Type': 'application/json',
                'Accept-Language': Shopify.locale,
                'X-Sig': window.sz_config.eth_key,
                'X-Epoch': window.sz_config.timestamp
              }
            })
                .then((response) => response.json())
                .then((resData) => {
                  if (!resData.errors) {
                    if (resData.data) { //create order success for both jp or non-jp
                      if (!resData.data.isJpPolicy) { // for non jp
                        popupOpen('popup_non_jp_order_success');
                        const qrEl = document.getElementById("eth_nonjp_qrcode");
                        qrEl.innerHTML = '';
                        const qr = new QRCode(qrEl, {
                          text: resData.data.paymentAddress,
                          width: 200,
                          height: 200,
                        });
                        setTimeout(() => {
                          const qrImage = document.querySelector('#eth_nonjp_qrcode img');
                          if (qrImage) {
                            const downloadBtn = document.getElementById('download-eth-nonjp-qrcode-btn');
                            downloadBtn.style.display = 'block';
                            downloadBtn.onclick = function() {
                              downloadQRCode(qrImage.src);
                            };
                          } else {
                            downloadBtn.style.display = 'none';
                          }
                        }, 500);
                        expireInCountDown(resData.data.expireIn);
                        document.querySelector('#eth_nonjp_amount').innerHTML = resData.data.amount + ' ETH';
                        document.querySelector('#eth_nonjp_summary_estimated_payment').innerHTML = resData.data.ethAmount + ' ETH';
                        document.querySelector('#eth_nonjp_summary_tax').innerHTML = Shopify.formatMoney(resData.data.tax*100, "{{amount_no_decimals}}")  + ' ' + resData.data.currency;
                        document.querySelector('#eth_nonjp_summary_orderamount').innerHTML =  Shopify.formatMoney(resData.data.totalPrice*100, "{{amount_no_decimals}}")  + ' ' + resData.data.currency;

                        document.querySelector('#eth_nonjp_account_wallet').innerHTML = resData.data.balance + ' ETH';
                        document.querySelector('#eth_nonjp_amount_deposited').innerHTML = resData.data.depositAmount + ' ETH';
                        document.querySelector('#eth_nonjp_tranfer_address').innerHTML = resData.data.paymentAddress;
                        const conversionRate = Shopify.formatMoney(resData.data.convertRate*100, "{{amount_no_decimals}}")  + ' ' + resData.data.currency;
                        document.querySelector('#eth_nonjp_summary_conversion_rate').innerText = `1 ETH ~ ${conversionRate}`;

                      } else {
                        popupOpen('popup_jp_order_success');
                        popupClose('popup_jp_received_address');
                        const qrEl = document.getElementById("eth_qrcode");
                        qrEl.innerHTML = '';
                        const qr = new QRCode(qrEl, {
                          text: resData.data.paymentAddress,
                          width: 200,
                          height: 200,
                        });
                        setTimeout(() => {
                          const qrImage = document.querySelector('#eth_qrcode img');
                          if (qrImage) {
                            const downloadBtn = document.getElementById('download-eth-qrcode-btn');
                            downloadBtn.style.display = 'block';
                            downloadBtn.onclick = function() {
                              downloadQRCode(qrImage.src);
                            };
                          } else {
                            downloadBtn.style.display = 'none';
                          }
                        }, 500);
                        expireInCountDown(resData.data.expireIn, true);
                        document.querySelector('#eth_amount_order').innerHTML = resData.data.ethAmount + ' ETH';
                        document.querySelector('#eth_summary_estimated_payment').innerHTML = resData.data.ethAmount + ' ETH';
                        document.querySelector('#eth_summary_tax').innerHTML = Shopify.formatMoney(resData.data.tax*100, "{{amount_no_decimals}}")  + ' ' + resData.data.currency;
                        document.querySelector('#eth_summary_orderamount').innerHTML =  Shopify.formatMoney(resData.data.totalPrice*100, "{{amount_no_decimals}}")  + ' ' + resData.data.currency;

                        document.querySelector('#eth_tranfer_address').innerHTML = resData.data.paymentAddress;
                        const conversionRate = Shopify.formatMoney(resData.data.convertRate*100, "{{amount_no_decimals}}")  + ' ' + resData.data.currency;
                        document.querySelector('#eth_summary_conversion_rate').innerText = `1 ETH ~ ${conversionRate}`;
                      }
                      trackPurchaseSuccess(resData.data.orderId, authJson, itemsCart);
                      ethOrderId = resData.data.orderId;
                      checkEthOrderStatus(resData.data.orderId);
                      clearEthCart();
                      loading_bg.style.display = "none";

                    } else {
                      if (resData.isJpPolicy) { // jp need update received address
                        popupOpen('popup_jp_received_address');
                        if (resData.receivedAddress) {
                          document.querySelector('#received-address').value = resData.receivedAddress;
                        }
                        loading_bg.style.display = "none";
                      }
                    }
                  } else {
                    if (resData.errors.hasPending) {
                      popupOpen('popup-has-pending');
                      document.querySelector('#popup-has-pending').querySelector('p').innerHTML = resData.errors.hasPending;
                    } else {
                      let errorMessage = Object.values(resData.errors)[0];

                      if (errorMessage === 'customer_must_verify_phone') {
                        popupOpen('popup-common-verified-phone');
                      } else {
                        let messageError;
                        if (errorMessage === 'product.outstock') {
                          let productPopup = document.querySelector('#popup-product-message');
                          const errorDetail = Object.values(resData.errors)[1] || '';
                          //productPopup.querySelector('.product_outofstock_name').innerHTML = errorDetail;
                          popupOpen('popup-product-message');
                          trackPurchaseFail(errorDetail);
                        } else {
                          messageError = document.querySelector('.eth-checkout-btn').getAttribute(`data-${errorMessage}`);
                          errorMessage = prepareCheckoutError(resData, errorMessage, window.CHECKOUT_ERROR_MESSAGES.OUTSTOCK);
                          popupOpen('popup-common-message');
                          commonPopup.querySelector('h4').innerHTML = commonPopup.querySelector('h4').getAttribute('data-errorTitle');
                          commonPopup.querySelector('p').innerHTML = messageError ? messageError : errorMessage;
                          trackPurchaseFail(messageError ? messageError : errorMessage);
                        }

                      }
                    }
                    popupClose('popup_jp_received_address');
                    loading_bg.style.display = "none";
                  }
                }).catch(console.error);
          } else {
            loading_bg.style.display = "none";
          }
        }).catch(console.error);
  }

  function checkEthOrderStatus(orderId) {
    ethOrderCheckStatusInterval = setInterval(() => {
      fetch(window.ETH_APP_URL + `api/order/status-check/${orderId}?customerId=${window.meta.page.customerId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': Shopify.locale,
          'X-Sig': window.sz_config.eth_key,
          'X-Epoch': window.sz_config.timestamp
        }
      })
          .then((response) => response.json())
          .then((response) => {
            if(response.orderStatus && response.orderStatus === 3) {
              //window.location.href = window.Shopify.routes.root + 'account';

              document.querySelector('#eth_success_order_number').innerHTML = response.orderNumber;
              document.querySelector('#eth_success_tax').innerHTML = Shopify.formatMoney(response.tax*100, "{{amount_no_decimals}}")  + ' ' + response.currency;
              document.querySelector('#eth_success_amount').innerHTML =  Shopify.formatMoney(response.totalPrice*100, "{{amount_no_decimals}}")  + ' ' + response.currency;
              document.querySelector('#eth_success_toaddress').innerHTML = response.toAddress;
              document.querySelector('#eth_success_totalpayment').innerHTML = response.ethAmount + ' ETH';
              const conversionRate = Shopify.formatMoney(response.convertRate*100, "{{amount_no_decimals}}")  + ' ' + response.currency;
              document.querySelector('#eth_success_convertrate').innerText = `1 ETH ~ ${conversionRate}`;
              document.querySelector('#eth_success_date').innerHTML = response.orderDate;
              popupClose('popup_non_jp_order_success');
              popupClose('popup_jp_order_success');
              popupOpen('popup-success-eth');
              clearInterval(ethOrderCheckStatusInterval);
            } else if (response.orderStatus && (response.orderStatus === 2 || response.orderStatus === 10)){
              window.location.href = window.Shopify.routes.root + 'account';
            }
          });
    }, 5000);
  }

  function expireInCountDown(expireIn, isJp = false) {
    var timeLimitInMinutes = expireIn / 60 / 1000;
    var timeLimitInSeconds = timeLimitInMinutes * 60;
    var timerElement = isJp ? document.getElementById('eth_awaiting_payment_order') : document.getElementById('eth_nonjp_awaiting_payment_order');

    if (expireTimeInterval) {
      clearInterval(expireTimeInterval);
    }
    function startTimer() {
      timeLimitInSeconds--;
      var minutes = Math.floor(timeLimitInSeconds / 60);
      var seconds = parseInt(timeLimitInSeconds % 60);
      if (timeLimitInSeconds < 0) {
        timerElement.textContent = '00:00';
        clearInterval(expireTimeInterval);
        timerElement.textContent = 'Expired!!!';
        return;
      }
      if (minutes < 10) {
        minutes = '0' + minutes;
      }
      if (seconds < 10) {
        seconds = '0' + seconds;
      }
      timerElement.textContent = minutes + ':' + seconds;
    }
    expireTimeInterval = setInterval(startTimer, 1000);
  }

  function clearEthCart() {
    fetch(window.Shopify.routes.root + 'cart/clear.js', {
      method: 'POST',
    })
        .then((response) => response.json())
        .then((response) => {
          if(response.item_count === 0) {
            document.dispatchEvent(new CustomEvent("dispatch:cart-drawer:refresh"));
          }
        });
  }
}
initEth();

function prepareCheckoutError(response, error, translate) {
  if (response.s_error?.line_items && response.s_error.line_items.includes('Unable to reserve inventory')) {
    error = translate;
  }
  return error;
}

var walletStatusInterval = 1;
function initCheckWallet() {
  let modalBtnwallet = document.getElementById("pay-web3-wallet");
  let loading_bg = document.querySelector(".loading_bg");
  let customerEmail = encodeURIComponent(window.customerEmail);
  if (modalBtnwallet) {
    modalBtnwallet.onclick = function(e){
      const customerId = window.meta.page.customerId;
      if (!customerId) {
        window.location.href = window.Shopify.routes.root + 'account';
        return;
      }

      // Check and save isReceivingContract state
      const isReceivingContractInput = document.querySelector('#is_receiving_contract');
      if (isReceivingContractInput) {
        saveIsReceivingContract(isReceivingContractInput.checked);
      }

      loading_bg.style.display = "block";

      purchaseCheck().then(purchase => {
        loading_bg.style.display = "none";
        if (purchase && !purchase.success) {
          // Map error code to user-friendly message
          const errorMessage = window.purchase_message && window.purchase_message[purchase.message]
              ? window.purchase_message[purchase.message]
              : purchase.message;
          showToast(errorMessage, 'error', 6000, {
            limit_value_jpy: purchase.limit_value_jpy,
            limit_duration_days: purchase.limit_duration_days
          });
          return;
        }

        inventoryCheck().then(inventory => {
          loading_bg.style.display = "none";
          if (inventory && inventory.agePriceRestriction && inventory.agePriceRestriction.blocked) {
            const reason = inventory.agePriceRestriction.reason || 'age_price_restricted';
            const ageRestrictionMessage = (window.purchase_message && window.purchase_message[reason])
              ? window.purchase_message[reason]
              : reason;
            showToast(ageRestrictionMessage, 'error', 6000);
            modalBtnwallet.removeAttribute('disabled');
            return;
          }
          if (inventory && inventory.purchaseTierRestriction && inventory.purchaseTierRestriction.blocked) {
            const restriction = inventory.purchaseTierRestriction;
            const tierRestrictionMessage = getPurchaseTierRestrictionMessage(restriction);
            showToast(tierRestrictionMessage, 'error', 6000);
            modalBtnwallet.removeAttribute('disabled');
            return;
          }
          if (inventory && inventory.outOfStockItems && inventory.outOfStockItems.length > 0) {

            const productPopup = document.querySelector('#popup-product-message');
            const itemNames = inventory.outOfStockItems.map(item =>
                `${item.name} (SKU: ${item.sku})`
            ).join(', ');
            //productPopup.querySelector('.product_outofstock_name').innerHTML = itemNames;
            popupOpen('popup-product-message');
            modalBtnwallet.removeAttribute('disabled');
            return;
          }

          if (inventory && inventory.activeSubs && inventory.activeSubs.length > 0) {
            popupOpen('popup-active-subs');
            modalBtnwallet.removeAttribute('disabled');
            return;
          }

          if (inventory && inventory.salonBlocked) {
            setupSalonRestrictionPopup(inventory.salonGachiSku, inventory.salonGachiStatus, inventory.salonRestrictedItems);
            popupOpen('popup-salon-restriction');
            modalBtnwallet.removeAttribute('disabled');
            return;
          }

          if (inventory && inventory.bundleBlocked) {
            const bundleMessage = inventory.bundleBlockReason === 'bundle_incomplete'
              ? theme.strings.subscriptionBundleIncomplete
              : theme.strings.subscriptionBundleBlocked;
            showToast(bundleMessage, 'error');
            modalBtnwallet.removeAttribute('disabled');
            return;
          }

          // Continue with normal checkout process if no out-of-stock items
          if (modalBtnwallet.classList.contains('customer-no-card')) {
            getCardInfo(customerId)
                .then(isCard => {
                  if (isCard) {
                    var authToken = getCookie(window.ENV == "DEV"? 'auth-token-test' : 'auth-token');

                    if (authToken) {
                      var authJson = JSON.parse(authToken);
                      if(authJson.access_token) {
                        getUserInfo(authJson.access_token)
                            .then(resData => {
                              if (resData){
                                if (resData.verify_phone_status){
                                  if (resData.is_agree_credix_term) {
                                    loading_bg.style.display = "block";
                                    fetch(`${window.middleware_api_url}/web3/v1/connect/check?email=${customerEmail}`, {
                                      method: 'GET',
                                      headers: {
                                        'Content-Type': 'application/json',
                                        'Accept-Language': Shopify.locale,
                                        'X-Sig': window.sz_config.middleware_key,
                                        'X-Epoch': window.sz_config.timestamp
                                      }
                                    })
                                        .then((response) => response.json())
                                        .then((response) => {
                                          window.location = `${Shopify.routes.root}pages/checkout-wallet`;
                                          if (response.success) {
                                            window.location = `${Shopify.routes.root}pages/checkout-wallet`;
                                          } else {
                                            connectWallet();
                                            popupOpen('popup_connect_wallet');
                                            checkwalletStatus(customerEmail);
                                          }
                                          loading_bg.style.display = "none";
                                        });
                                  } else {
                                    popupOpen('popup-common-term-condition');
                                  }
                                } else {
                                  popupOpen('popup-common-verified-phone');
                                }
                              }
                            });
                      }
                    }


                  } else {
                    popupOpen('popup-common-secure-payment');
                    return;
                  }
                });
          } else {
            var authToken = getCookie(window.ENV == "DEV"? 'auth-token-test' : 'auth-token');

            if (authToken) {
              var authJson = JSON.parse(authToken);
              if(authJson.access_token) {
                getUserInfo(authJson.access_token)
                    .then(resData => {
                      if (resData){
                        if (resData.verify_phone_status){
                          loading_bg.style.display = "block";
                          fetch(`${window.middleware_api_url}/web3/v1/connect/check?email=${customerEmail}`, {
                            method: 'GET',
                            headers: {
                              'Content-Type': 'application/json',
                              'Accept-Language': Shopify.locale,
                              'X-Sig': window.sz_config.middleware_key,
                              'X-Epoch': window.sz_config.timestamp
                            }
                          })
                              .then((response) => response.json())
                              .then((response) => {
                                window.location = `${Shopify.routes.root}pages/checkout-wallet`;
                                // if (response.success) {
                                //   window.location = `${Shopify.routes.root}pages/checkout-wallet`;
                                // } else {
                                //   connectWallet();
                                //   popupOpen('popup_connect_wallet');
                                //   checkwalletStatus(customerEmail);
                                // }
                                loading_bg.style.display = "none";
                              });
                        } else {
                          popupOpen('popup-common-verified-phone');
                        }
                      }
                    });
              }
            }
          }
        });

      });


    }
    window.addEventListener( "pageshow", function ( event ) {
      var historyTraversal = event.persisted ||
          ( typeof window.performance != "undefined" &&
              window.performance.navigation.type === 2 );
      if ( historyTraversal ) {
        // Handle page restore.
        //window.location.reload();
      }
    });
  }

  function checkwalletStatus(email) {
    walletStatusInterval = setInterval(() => {
      fetch(`${window.middleware_api_url}/web3/v1/connect/check?email=${email}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': Shopify.locale,
          'X-Sig': window.sz_config.middleware_key,
          'X-Epoch': window.sz_config.timestamp
        }
      })
          .then((response) => response.json())
          .then((response) => {
            if (response.success) {
              window.location = `${Shopify.routes.root}pages/checkout-wallet`;
            }
          });
    }, 5000);
  }

  async function connectWallet() {
    try {
      const response = await fetch(`${window.middleware_api_url}/web3/v1/connect/get-hash?email=${customerEmail}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': Shopify.locale,
          'X-Sig': window.sz_config.middleware_key,
          'X-Epoch': window.sz_config.timestamp
        }
      });
      const data = await response.json();
      if (data.hash) {
        const expiredTime = data.expiredTime;
        generateQRCode(data.hash);

        // Calculate the time until the QR code expires in milliseconds
        const timeUntilExpiration = new Date(expiredTime).getTime() - new Date().getTime();

        console.log(timeUntilExpiration);

        // Set a timeout to regenerate the QR code when it expires
        setTimeout(connectWallet, timeUntilExpiration);
      }
    } catch (err) {
      console.error(err);
    }
    return null;
  }

  function generateQRCode(hash) {
    const qrWallet = document.getElementById("wallet_qrcode");
    qrWallet.innerHTML = '';
    const qr = new QRCode(qrWallet, {
      text: hash,
      width: 200,
      height: 200,
    });

    setTimeout(() => {
      const qrImage = document.querySelector('#wallet_qrcode img');
      if (qrImage) {
        const downloadBtn = document.getElementById('download-qrcode-btn');
        downloadBtn.style.display = 'block';
        downloadBtn.onclick = function() {
          downloadQRCode(qrImage.src);
        };
      } else {
        downloadBtn.style.display = 'none';
      }
    }, 500);
  }

  function downloadQRCode(qrSrc) {
    const a = document.createElement('a');
    a.href = qrSrc;
    a.download = 'qr_code.png';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }


}
initCheckWallet();

async function getUserInfo(accessToken) {
  const url = `${window.middleware_api_url}/sso/v1/user-info`;
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept-Language': Shopify.locale,
        'X-Sig': window.sz_config.middleware_key,
        'X-Epoch': window.sz_config.timestamp
      },
      body: JSON.stringify({ access_token: accessToken, email: window.customerEmail || '' })
    });

    const result = await response.json();

    // Explicitly check for "code": 401 and redirect to logout
    if (result.code === 401) {
      // Redirect to logout after showing popup
      setTimeout(() => {
        popupClose('popup-add-term-condition');
        popupClose('popup-common-term-condition');
        popupOpen('popup-common-session-expired');
      }, 1500);
      setTimeout(() => {
        window.location.href = "/account/logout";
      }, 5000);
      return false;
    }

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    if (result.status && result.data) {
      const isAgreeCredixTerm = result.data.is_agree_credix_term;
      const isVerifyPhoneStatus = result.data.verify_phone_status;
      return result.data;
    } else {
      console.error('Invalid response format:', result);
      return false;
    }
  } catch (error) {
    console.error('Error fetching user info:', error);
    return false;
  }
}

async function getCardInfo(customerId) {
  const url = `${window.SHOPIFY_SUB_APP}/3ds/card/${customerId}`;
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept-Language': Shopify.locale,
        'X-Sig': window.sz_config.sub_app_key,
        'X-Epoch': window.sz_config.timestamp
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const result = await response.json();

    if (result.data) {
      return true;
    } else {
      console.error('Invalid response format:', result);
      return false;
    }
  } catch (error) {
    console.error('Error fetching user info:', error);
    return false;
  }
}

async function getConfirmPolicy(accessToken) {
  const url = `${window.middleware_api_url}/sso/V1/confirm-credix-policy`;
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept-Language': Shopify.locale,
        'X-Sig': window.sz_config.middleware_key,
        'X-Epoch': window.sz_config.timestamp
      },
      body: JSON.stringify({ access_token: accessToken, email: window.customerEmail || '' })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const result = await response.json();

    if (result.status && result.data) {
      return true;
    } else {
      return false;
    }
  } catch (error) {
    console.error('Error fetching user info:', error);
    return false;
  }
}


function initConfirmPolicy() {
  let modalBtnverify = document.querySelector(".verify-term-condition");
  let modalBtnaddcardverify = document.querySelector(".btn-add-card-verify");
  let modalBtnverifyorderresume = document.querySelector(".verify-tc-order-resume");
  let modalBtnsessionexpired = document.querySelector(".btn-cancel-session-expired");
  let modalBtnaddcart = document.querySelector(".btn-add-card");
  let loading_bg = document.querySelector(".loading_bg");
  let customerEmail = encodeURIComponent(window.customerEmail);
  if (modalBtnverify) {
    modalBtnverify.onclick = function(e){
      loading_bg.style.display = "block";
      var authToken = getCookie(window.ENV == "DEV"? 'auth-token-test' : 'auth-token');
      console.log(authToken);
      if (authToken) {
        var authJson = JSON.parse(authToken);
        if(authJson.access_token) {
          getConfirmPolicy(authJson.access_token)
              .then(isAgreed => {
                if (isAgreed) {
                  const customerId = window.meta.page.customerId;
                  getCardInfo(customerId)
                      .then(isCard => {
                        if (isCard) {
                          window.location = `${Shopify.routes.root}pages/checkout-credit-card`;
                        } else {
                          const isReceivingContractInput = document.querySelector('#is_receiving_contract');
                          const confirmContractBtn = document.querySelector('#confirm-contract-btn');
                          if (isReceivingContractInput && confirmContractBtn) {
                            if (isReceivingContractInput.checked) {
                              // Save isReceivingContract state
                              saveIsReceivingContract(true);

                              confirmContractBtn.setAttribute('data-payment-method', 'credix_first_purchase');
                              const cartDrawer = document.getElementById('cart-drawer');
                              if (cartDrawer && typeof cartDrawer.close === 'function') {
                                cartDrawer.close();
                              }
                              popupClose('popup-common-term-condition');

                              // Load and populate sale info before opening popup
                              const contractInfoElement = document.querySelector('contract-information');
                              if (contractInfoElement && typeof contractInfoElement.getSaleInfo === 'function') {
                                contractInfoElement.getSaleInfo(customerId)
                                    .then((saleInfoResult) => {
                                      if (saleInfoResult.success && saleInfoResult.exists && saleInfoResult.data) {
                                        if (typeof contractInfoElement.populateFormFromSaleInfo === 'function') {
                                          contractInfoElement.populateFormFromSaleInfo(saleInfoResult.data);
                                        }
                                      }
                                      // Open popup after populating form
                                      popupOpen('popup_contract_information');
                                    })
                                    .catch((error) => {
                                      console.error('Error loading sale info:', error);
                                      // Still open popup even if API fails
                                      popupOpen('popup_contract_information');
                                    });
                              } else {
                                // Fallback: open popup directly if contract-information element not found
                                popupOpen('popup_contract_information');
                              }
                              return;
                            } else {
                              confirmContractBtn.removeAttribute('data-payment-method');
                              // Save isReceivingContract state when unchecked
                              saveIsReceivingContract(false);
                            }
                          }
                          window.location = `${Shopify.routes.root}pages/3ds-checkout`;
                        }
                      });
                }
                loading_bg.style.display = "none";
              });
        }
      }
    }

  }

  if (modalBtnsessionexpired) {
    modalBtnsessionexpired.onclick = function(e){
      window.location.href = "/account/logout";
    }
  }

  if (modalBtnverifyorderresume) {
    modalBtnverifyorderresume.onclick = function(e){
      loading_bg.style.display = "block";
      var authToken = getCookie(window.ENV == "DEV"? 'auth-token-test' : 'auth-token');
      let resumeOrderId = document.querySelector('.verify-tc-order-resume').getAttribute('data-order-id');
      if (authToken) {
        var authJson = JSON.parse(authToken);
        if(authJson.access_token) {
          getConfirmPolicy(authJson.access_token)
              .then(isAgreed => {
                if (isAgreed) {
                  const customerId = window.meta.page.customerId;
                  getCardInfo(customerId)
                      .then(isCard => {
                        if (isCard) {
                          window.location = `${Shopify.routes.root}pages/checkout-credit-card?resumeOrderId=${resumeOrderId }`;
                        } else {
                          window.location = `${Shopify.routes.root}pages/3ds-checkout?resumeOrderId=${resumeOrderId }`;
                        }
                      });
                }
                document.querySelector('#popup-common-term-condition').classList.remove('tc-order-resume-popup');
                loading_bg.style.display = "none";
              });
        }
      }
    }
    document.querySelector('#popup-common-term-condition .btn-cancel-popup').addEventListener('click', (e) => {
      document.querySelector('#popup-common-term-condition').classList.remove('tc-order-resume-popup');
    });
    document.querySelector('#popup-common-term-condition .close-btn').addEventListener('click', (e) => {
      document.querySelector('#popup-common-term-condition').classList.remove('tc-order-resume-popup');
    });

  }


  if (modalBtnaddcart) {
    modalBtnaddcart.onclick = function(e){

      var authToken = getCookie(window.ENV == "DEV"? 'auth-token-test' : 'auth-token');
      if (authToken) {
        var authJson = JSON.parse(authToken);
        if(authJson.access_token) {
          getUserInfo(authJson.access_token)
              .then(resData => {
                if (resData){
                  if (resData.verify_phone_status){
                    if (resData.is_agree_credix_term) {
                      const customerId = window.meta.page.customerId;
                      loading_bg.style.display = "block";
                      popupClose('popup-common-secure-payment');
                      window.location = `${Shopify.routes.root}pages/3ds-update-card`;
                      // fetch(`${window.SHOPIFY_SUB_APP}/3ds/shop/create-card-url`, {
                      //   method: 'POST',
                      //   body: JSON.stringify({ customerId: customerId }),
                      //   headers: {
                      //     'Content-Type': 'application/json',
                      //     'Accept-Language': Shopify.locale,
                      //     'X-Sig': window.sz_config.sub_app_key,
                      //     'X-Epoch': window.sz_config.timestamp
                      //   }
                      // }).then((response) => response.json()).then((response) => {
                      //   if (response.payment_url) {
                      //     window.location = response.payment_url;
                      //   }
                      //   loading_bg.style.display = "none";
                      // }).catch((err) => {
                      //   loading_bg.style.display = "none";
                      //   console.error(err);
                      // });
                    } else {
                      popupClose('popup-common-secure-payment');
                      popupOpen('popup-add-term-condition');
                    }
                  } else {
                    popupClose('popup-common-secure-payment');
                    popupOpen('popup-common-verified-phone');
                  }
                }
              });
        }
      }

    }
  }

  if (modalBtnaddcardverify) {
    modalBtnaddcardverify.onclick = function(e){
      loading_bg.style.display = "block";
      var authToken = getCookie(window.ENV == "DEV"? 'auth-token-test' : 'auth-token');
      if (authToken) {
        var authJson = JSON.parse(authToken);
        if(authJson.access_token) {
          getConfirmPolicy(authJson.access_token)
              .then(isAgreed => {
                if (isAgreed) {
                  window.location = `${Shopify.routes.root}pages/3ds-update-card`;
                  // const customerId = window.meta.page.customerId;
                  // fetch(`${window.SHOPIFY_SUB_APP}/3ds/shop/create-card-url`, {
                  //   method: 'POST',
                  //   body: JSON.stringify({ customerId: customerId }),
                  //   headers: {
                  //     'Content-Type': 'application/json',
                  //     'Accept-Language': Shopify.locale,
                  //     'X-Sig': window.sz_config.sub_app_key,
                  //     'X-Epoch': window.sz_config.timestamp
                  //   }
                  // }).then((response) => response.json()).then((response) => {
                  //   if (response.payment_url) {
                  //     window.location = response.payment_url;
                  //   }
                  //   loading_bg.style.display = "none";
                  // }).catch((err) => {
                  //   loading_bg.style.display = "none";
                  //   console.error(err);
                  // });
                }
              });
        }
      }

    }
  }


}
initConfirmPolicy();

function trackPurchaseSuccess(orderId, authJson, itemsCart) {
  // Push to dataLayer
  dataLayer.push({
    event: "purchase",
    ecommerce: {
      transaction_id: orderId || '',
      currency: Shopify.currency.active || '',
      buying_duration: getTimeSinceFirstVisit() || '',
      sso_user_id: authJson.user?.id || '',
      user_email: window.customerEmail || '',
      items: itemsCart || []
    }
  });
}

function trackCheckOut(payment_type, authJson, itemsCart) {
  // Push to dataLayer
  dataLayer.push({ ecommerce: null });
  dataLayer.push({
    event: "begin_checkout",
    user_id: authJson.user?.id || '',
    ecommerce: {
      payment_type: payment_type || '',
      buying_duration: getTimeSinceFirstVisit() || '',
      sso_user_id: authJson.user?.id || '',
      user_email: window.customerEmail || '',
      items: itemsCart || []
    }
  });
}

function trackRemoveFromCart(authJson, itemsCart) {
  // Push to dataLayer
  dataLayer.push({ ecommerce: null });
  dataLayer.push({
    event: "remove_from_cart",
    user_id: authJson.user?.id || '',
    ecommerce: {
      sso_user_id: authJson.user?.id || '',
      user_email: window.customerEmail || '',
      avatar_id: itemsCart.id,
      avatar_sku: itemsCart.sku,
      avatar_type: itemsCart.avatar_type,
      avatar_price: itemsCart.price,
      buying_duration: getTimeSinceFirstVisit(),
      avatar_quantity: itemsCart.quantity
    }
  });
}

function trackPurchaseFail(errorDetail) {
  // Push to dataLayer
  dataLayer.push({ ecommerce: null });
  dataLayer.push({
    event: "purchase_fail",
    ecommerce: {
      fail_reason: errorDetail
    }
  });
}

function getTimeSinceFirstVisit() {
  var firstVisit = localStorage.getItem('first_visit_timestamp');
  var now = new Date().getTime();

  if (!firstVisit) {
    // If no timestamp exists, set it now
    localStorage.setItem('first_visit_timestamp', now);
    return 'New User'; // First-time visitor
  }

  var timeSinceFirstVisit = (now - firstVisit) / 1000; // Convert milliseconds to seconds

  // Define duration categories based on time since first visit
  if (timeSinceFirstVisit < 86400) { // Less than 1 day
    return '1 day';
  } else if (timeSinceFirstVisit < 604800) { // Less than 1 week
    return '1 week';
  } else if (timeSinceFirstVisit < 2592000) { // Less than 1 month
    return '1 month';
  } else {
    return 'More than 1 month';
  }
}

function initFirstVisit() {
  var firstVisit = localStorage.getItem('first_visit_timestamp');

  if (!firstVisit) {
    // First visit, save the timestamp
    localStorage.setItem('first_visit_timestamp', new Date().getTime());
  }
}
initFirstVisit();

// Upgrade checks expect data.subscription_upgrade = { sku, variant_id }.
function getSubscriptionUpgradeProduct(data) {
  const responseData = data && data.data ? data.data : {};
  const upgradeProduct = responseData.subscription_upgrade || {};
  const sku = upgradeProduct.sku || null;
  const variantId = upgradeProduct.variant_id || null;
  const normalizedVariantId = Number(variantId);
  const blocked = upgradeProduct.blocked === true;

  if (blocked) {
    // The backend decided this upgrade must not proceed at all (e.g. still within a prior
    // upgrade's free-months window) — the caller must abort add-to-cart, not just skip the
    // variant swap. block_reason is a machine code; the UI text lives in theme locale files.
    return {
      blocked: true,
      blockReason: upgradeProduct.block_reason || null,
      sku: null,
      variantId: null
    };
  }

  if (!sku || !Number.isInteger(normalizedVariantId) || normalizedVariantId <= 0) {
    return null;
  }

  return {
    blocked: false,
    sku: sku,
    variantId: normalizedVariantId
  };
}


// Bundle checks expect data.bundle_resolution = { blocked, block_reason, items: [{role, sku, variant_id}] }.
// Fails CLOSED: any unexpected shape is treated as blocked, never as "no bundle applies" —
// unlike getSubscriptionUpgradeProduct, there is no valid "do nothing" outcome for a bundle
// add-to-cart click, so ambiguity must never silently proceed to a full-price/unfiltered add.
function getBundleResolutionItems(data) {
  const responseData = data && data.data ? data.data : {};
  const resolution = responseData.bundle_resolution;

  if (!resolution || typeof resolution !== 'object') {
    return { blocked: true, blockReason: 'missing_bundle_resolution', items: [] };
  }

  if (resolution.blocked === true) {
    return { blocked: true, blockReason: resolution.block_reason || null, items: [] };
  }

  const items = (Array.isArray(resolution.items) ? resolution.items : []).map((item) => {
    const rawVariantId = item.variant_id;
    const numericVariantId = Number(rawVariantId);
    const variantId = rawVariantId != null && Number.isInteger(numericVariantId) && numericVariantId > 0
      ? numericVariantId
      : null;
    return { role: item.role || null, sku: item.sku || null, variantId };
  });

  return { blocked: false, blockReason: null, items };
}

// Unlike subscriptionUpgradeCheck (which returns null on error, treated as "no upgrade, proceed
// normally"), this returns blocked: true on error — fail closed, matching getBundleResolutionItems.
async function bundleEligibilityCheck(bundleSku, defaultProducts) {
  try {
    const inventoryRequestData = {
      email: window.customerEmail || '',
      bundle_sku: bundleSku,
      product: defaultProducts, // [{sku, qty, price}, ...] - the bundle's full default composition
    };

    const inventoryResponse = await fetch(`${window.middleware_api_url}/suzu/common/v2/inventory-check`, {
      method: 'POST',
      body: JSON.stringify(inventoryRequestData),
      headers: {
        'Content-Type': 'application/json',
        'Accept-Language': Shopify.locale,
        'X-Sig': window.sz_config.middleware_key,
        'X-Epoch': window.sz_config.timestamp
      }
    });

    const data = await inventoryResponse.json();
    return getBundleResolutionItems(data);
  } catch (err) {
    console.error('Error in bundleEligibilityCheck:', err);
    return { blocked: true, blockReason: 'request_failed', items: [] };
  }
}

// BUZ-535 follow-up #2: with the DM-side fallthrough fix, a non-blocked
// check_subscription_upgrade=true response also carries active_subs from the backend's real
// duplicate check (getActiveSubscriptionsByEmail) — "you already hold this subscription active."
function getActiveSubsFromInventoryCheck(data) {
  const responseData = data && data.data ? data.data : {};
  return Array.isArray(responseData.active_subs) ? responseData.active_subs : [];
}

async function subscriptionUpgradeCheck(sku, quantity = 1) {
  try {
    const inventoryRequestData = {
      email: window.customerEmail || '',
      product: [{sku: sku, qty: quantity}],
      check_subscription_upgrade: true
    };

    const inventoryResponse = await fetch(`${window.middleware_api_url}/suzu/common/v2/inventory-check`, {
      method: 'POST',
      body: JSON.stringify(inventoryRequestData),
      headers: {
        'Content-Type': 'application/json',
        'Accept-Language': Shopify.locale,
        'X-Sig': window.sz_config.middleware_key,
        'X-Epoch': window.sz_config.timestamp
      }
    });

    const data = await inventoryResponse.json();
    return {
      upgradeProduct: getSubscriptionUpgradeProduct(data),
      activeSubs: getActiveSubsFromInventoryCheck(data)
    };
  } catch (err) {
    console.error('Error in subscriptionUpgradeCheck:', err);
  }
  return null;
}

// Salon purchase restriction (BUZ-293): eligibility for the Gachi plan is decided
// by the backend Gachi status API. The theme calls it when a Gachi SKU is in the
// cart and opens #popup-salon-restriction when checkout is blocked.
//
// The gated SKU list is driven entirely by the theme setting gachi_gate_skus
// (window.gachi_gate_skus, comma-separated; default 6 SKUs live in settings_schema.json).
// If the setting is empty/unset the gate is simply INACTIVE — there is no hardcoded
// fallback. The Lite plan (KING_SALON_1M / KING_SALON_1Y) is never listed.
function getGachiGateSkus() {
  const raw = window.gachi_gate_skus;
  if (typeof raw === 'string') {
    return raw.split(',').map((s) => s.trim()).filter(Boolean);
  }
  if (Array.isArray(raw)) {
    return raw.map((s) => String(s).trim()).filter(Boolean);
  }
  return [];
}

// Storefront-signed headers for the Gachi APIs. Reuses the same shop/epoch/customer
// HMAC that footer.liquid already renders into window.sz_config (see getSaleInfo).
function getGachiRequestHeaders() {
  return {
    'Content-Type': 'application/json',
    'Origin': window.location.origin,
    'Accept-Language': Shopify.locale || 'ja',
    'X-Sig': window.sz_config?.shopify_server_key,
    'X-Epoch': window.sz_config?.timestamp
  };
}

function getGachiCustomerId() {
  // customer.id is what the X-Sig signature is computed over (footer.liquid),
  // so the body customerId must use the same value.
  return (window.meta && window.meta.page && window.meta.page.customerId)
    || window.customerId
    || '';
}

// POST /sale/gachi/status — returns the backend verdict for the given Gachi SKU.
// Fail-open: returns null on any error / missing config / guest (no customerId),
// so checkout is never blocked while the API is unavailable (e.g. not yet deployed).
async function getGachiStatus(sku) {
  try {
    const customerId = getGachiCustomerId();
    const email = window.customerEmail || '';

    if (!sku || !customerId || !window.shopify_server_url) {
      return null;
    }

    const response = await fetch(`${window.shopify_server_url}/sale/gachi/status`, {
      method: 'POST',
      headers: getGachiRequestHeaders(),
      body: JSON.stringify({ email: email, sku: sku, customerId: String(customerId) })
    });

    const data = await response.json();

    if (!response.ok || !data || data.status !== true || !data.data) {
      return null;
    }

    // { eligible, decision, reason, requestStatus, canSubmitRequest, request? }
    return data.data;
  } catch (err) {
    console.error('Error in getGachiStatus:', err);
    return null; // fail-open
  }
}

// POST /sale/gachi/request — creates an upgrade/interview request (idempotent).
// Returns the request data or null on error.
async function submitGachiRequest(sku) {
  try {
    const customerId = getGachiCustomerId();
    const email = window.customerEmail || '';

    if (!sku || !customerId || !window.shopify_server_url) {
      return null;
    }

    // Gate "Requesting" record (drives the status flow). The interview-form data goes
    // to the separate new Zendesk endpoint (submitGachiForm).
    const response = await fetch(`${window.shopify_server_url}/sale/gachi/request`, {
      method: 'POST',
      headers: getGachiRequestHeaders(),
      body: JSON.stringify({ email: email, sku: sku, customerId: String(customerId) })
    });

    const data = await response.json();

    if (!response.ok || !data || data.status !== true || !data.data) {
      return null;
    }

    return data.data;
  } catch (err) {
    console.error('Error in submitGachiRequest:', err);
    return null;
  }
}

// R1: localized failure message for the Gachi request toast (injected by footer.liquid
// as window.salon_request_failed_message; JP fallback if unavailable).
// POST the interview form to the dedicated Zendesk-ticket endpoint (separate from
// /sale/gachi/request, which only creates the gate record). The BE turns these fields
// into a Zendesk ticket. Body field names follow the BUZ-293 storefront API spec.
const GACHI_FORM_PATH = '/sale/gachi/zendesk-ticket';
async function submitGachiForm(sku, fields) {
  try {
    const customerId = getGachiCustomerId();
    const email = (fields && fields.email) || window.customerEmail || '';

    if (!sku || !customerId || !window.shopify_server_url) {
      return null;
    }

    // 希望日ごとの時間帯レンジ配列（最大3件）を送る。空欄は除外。
    const timeslots = (fields && Array.isArray(fields.timeslots))
      ? fields.timeslots.filter(function (v) { return v; })
      : [];

    // Canonical field names per the BUZ-293 spec (fullName / phoneNumber / upgradeReason
    // / timeslots). sku is included so the BE can append it to the Zendesk ticket body.
    const body = {
      email: email,
      sku: sku,
      customerId: String(customerId),
      fullName: (fields && fields.full_name) || '',
      phoneNumber: (fields && fields.phone_number) || '',
      occupation: (fields && fields.occupation) || '',
      upgradeReason: (fields && fields.reason) || '',
      timeslots: timeslots,
      // Backend Zendesk API supports only 'en' and 'ja' (default 'en'); map JP locale -> 'ja', else 'en'.
      lang: (String(Shopify.locale || '').toLowerCase().indexOf('ja') === 0) ? 'ja' : 'en'
    };

    const response = await fetch(`${window.shopify_server_url}${GACHI_FORM_PATH}`, {
      method: 'POST',
      headers: getGachiRequestHeaders(),
      body: JSON.stringify(body)
    });

    const data = await response.json();
    if (!response.ok || !data || data.status !== true) {
      return null;
    }
    return data;
  } catch (err) {
    console.error('Error in submitGachiForm:', err);
    return null;
  }
}

function getSalonRequestFailedMessage() {
  return window.salon_request_failed_message || '申請に失敗しました。時間をおいてもう一度お試しください。';
}

// Renders the restricted (Gachi) cart item names into the popup. Uses textContent
// to avoid injecting product titles as HTML.
function renderSalonRestrictedItems(popup, items) {
  const wrap = popup.querySelector('.salon-restricted-items');
  const namesEl = popup.querySelector('.salon-restricted-items__names');
  if (!wrap || !namesEl) return;

  const list = Array.isArray(items) ? items.filter(Boolean) : [];
  const names = list.map((it) => (it && (it.name || it.sku)) || '').filter(Boolean);

  if (names.length === 0) {
    namesEl.textContent = '';
    wrap.style.display = 'none';
    return;
  }

  namesEl.textContent = names.join('、');
  wrap.style.display = '';
}

// Renders a status-specific message (Requesting/Reviewing) in place of the default
// body, and hides the interview booking link while a request is under review.
function renderSalonStatusMessage(popup, status) {
  const defaultMsg = popup.querySelector('.salon-default-message');
  const statusMsg = popup.querySelector('.salon-status-message');
  const requestStatus = status && status.requestStatus;
  const messages = window.salon_status_messages || {};
  const text = requestStatus ? messages[requestStatus] : '';

  if (defaultMsg) defaultMsg.style.display = '';
  if (statusMsg) { statusMsg.style.display = 'none'; statusMsg.textContent = ''; }

  // Requesting / Reviewing → show the status message instead of the default body.
  // (Form visibility is handled separately by setupSalonRequestForm.)
  if (text && statusMsg) {
    if (defaultMsg) defaultMsg.style.display = 'none';
    statusMsg.textContent = text;
    statusMsg.style.display = '';
  }
}

// Opens/sets up the salon restriction popup: status message, restricted items, and
// the interview-request FORM (shown only when the customer may submit a request).
function setupSalonRestrictionPopup(sku, status, items) {
  const popup = document.getElementById('popup-salon-restriction');
  if (!popup) return;

  renderSalonStatusMessage(popup, status);
  renderSalonRestrictedItems(popup, items);
  setupSalonRequestForm(popup, sku, status);
}

function pad2(n) { return (n < 10 ? '0' : '') + n; }

// 時刻 <select> に 9:00〜22:00・30分刻みの選択肢を一度だけ充填。
var SALON_TIME_START_HOUR = 9;
var SALON_TIME_END_HOUR = 22;
var SALON_TIME_STEP_MIN = 30;
function fillTimeOptions(select) {
  if (!select || select.options.length > 0) return;
  const ph = document.createElement('option');
  ph.value = '';
  ph.textContent = '--:--';
  select.appendChild(ph);
  for (let h = SALON_TIME_START_HOUR; h <= SALON_TIME_END_HOUR; h++) {
    for (let m = 0; m < 60; m += SALON_TIME_STEP_MIN) {
      if (h === SALON_TIME_END_HOUR && m > 0) break; // 22:00 で打ち止め
      const t = pad2(h) + ':' + pad2(m);
      const opt = document.createElement('option');
      opt.value = t;
      opt.textContent = t;
      select.appendChild(opt);
    }
  }
}

// 面談希望日の下限 = 翌日起算2営業日後（土日祝休業）。祝日出典: 内閣府/holidays-jp。収録2026-2027、超過は土日のみ反映。
var JP_HOLIDAYS = new Set([
  '2026-01-01', '2026-01-12', '2026-02-11', '2026-02-23', '2026-03-20', '2026-04-29',
  '2026-05-03', '2026-05-04', '2026-05-05', '2026-05-06', '2026-07-20', '2026-08-11',
  '2026-09-21', '2026-09-22', '2026-09-23', '2026-10-12', '2026-11-03', '2026-11-23',
  '2027-01-01', '2027-01-11', '2027-02-11', '2027-02-23', '2027-03-21', '2027-03-22',
  '2027-04-29', '2027-05-03', '2027-05-04', '2027-05-05', '2027-07-19', '2027-08-11',
  '2027-09-20', '2027-09-23', '2027-10-11', '2027-11-03', '2027-11-23'
]);
var JP_HOLIDAYS_RANGE_END = '2027-12-31';
var SALON_MIN_BUSINESS_DAYS = 2;
var jpHolidayRangeWarned = false;
function formatYmd(d) { return d.getFullYear() + '-' + pad2(d.getMonth() + 1) + '-' + pad2(d.getDate()); }
function isBusinessDay(d) {
  var wd = d.getDay();
  if (wd === 0 || wd === 6) return false;
  var ymd = formatYmd(d);
  if (ymd > JP_HOLIDAYS_RANGE_END) {
    if (!jpHolidayRangeWarned) { jpHolidayRangeWarned = true; console.warn('[salon-form] JP_HOLIDAYS out of range >' + JP_HOLIDAYS_RANGE_END); }
    return true;
  }
  return !JP_HOLIDAYS.has(ymd);
}
function addBusinessDays(from, n) {
  var d = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  var counted = 0;
  while (counted < n) { d.setDate(d.getDate() + 1); if (isBusinessDay(d)) counted++; }
  return d;
}
function getSalonMinBookingDate() { return formatYmd(addBusinessDays(new Date(), SALON_MIN_BUSINESS_DAYS)); }
function getSalonMinDateMessage() { return window.salon_min_date_message || '2営業日以降の日付を選択してください。'; }
function getSalonTimeRequiredMessage() { return window.salon_time_required_message || '各希望日は日付と時間の両方をご入力ください。'; }
function getSalonDateDuplicateMessage() { return window.salon_date_duplicate_message || '他の希望日と同じ日付は選べません。'; }
function getSalonTimeRangeInvalidMessage() { return window.salon_time_range_invalid_message || '終了時間は開始時間の1時間以上後を選択してください。'; }
function toMinutes(tm) { var p = String(tm).split(':'); return parseInt(p[0], 10) * 60 + (parseInt(p[1], 10) || 0); }
function salonGroupVal(group, selector) { var el = group.querySelector(selector); return el ? String(el.value || '').trim() : ''; }
// "YYYY-MM-DD" → "YYYY年M月D日"（Zendesk 表示用）。
function formatJpDate(dt) {
  var p = String(dt).split('-');
  return (p.length === 3) ? parseInt(p[0], 10) + '年' + parseInt(p[1], 10) + '月' + parseInt(p[2], 10) + '日' : dt;
}
var SALON_MIN_RANGE_MINUTES = 60; // 希望時間帯の最小幅（面談1時間分）
// 開始に応じて終了の選択肢を「開始＋最小幅」以降のみ有効化。無効域に落ちた終了はクリア。
function updateSalonEndOptions(group) {
  if (!group) return;
  var endSel = group.querySelector('.salon-day-end');
  if (!endSel) return;
  var sv = salonGroupVal(group, '.salon-day-start');
  var minEnd = sv ? toMinutes(sv) + SALON_MIN_RANGE_MINUTES : 0;
  Array.prototype.forEach.call(endSel.options, function (opt) {
    if (opt.value) opt.disabled = !!sv && toMinutes(opt.value) < minEnd;
  });
  if (endSel.value && sv && toMinutes(endSel.value) < minEnd) endSel.selectedIndex = 0;
}
// 日付重複は即トースト＋クリア、開始変更時は終了選択肢を制限（form.onchange から委譲）。
function handleSalonFieldChange(form, target) {
  var v = String(target.value || '').trim();
  if (v && target.classList.contains('salon-day-date')) {

    if (v < getSalonMinBookingDate()) {
      if (v !== formatYmd(new Date())) showToast(getSalonMinDateMessage(), 'error');
      target.value = '';
    } else {
      var dup = Array.prototype.some.call(form.querySelectorAll('.salon-day-date'), function (o) {
        return o !== target && String(o.value || '').trim() === v;
      });
      if (dup) { showToast(getSalonDateDuplicateMessage(), 'error'); target.value = ''; }
    }
  }
  if (target.classList.contains('salon-day-start')) {
    updateSalonEndOptions(target.closest('.salon-daygroup'));
  }
}
// 希望日ごとに "YYYY年M月D日 HH:MM～HH:MM" を配列で返す（最大3件・希望順）。
function collectSalonTimeslots(form) {
  var out = [];
  form.querySelectorAll('.salon-daygroup').forEach(function (group) {
    var dt = salonGroupVal(group, '.salon-day-date');
    var st = salonGroupVal(group, '.salon-day-start');
    var en = salonGroupVal(group, '.salon-day-end');
    if (dt && st && en) out.push(formatJpDate(dt) + ' ' + st + '～' + en);
  });
  return out;
}
// 3点セット欠け / 下限前 / 日付重複 / 幅不足 / 0件 を検証。NG ならメッセージ、OK なら ''。
function getSalonTimeslotsError(form, minDate) {
  var seen = [];
  var groups = form.querySelectorAll('.salon-daygroup');
  for (var i = 0; i < groups.length; i++) {
    var dt = salonGroupVal(groups[i], '.salon-day-date');
    var st = salonGroupVal(groups[i], '.salon-day-start');
    var en = salonGroupVal(groups[i], '.salon-day-end');
    if (!dt && !st && !en) continue;
    if (!dt || !st || !en) return getSalonTimeRequiredMessage();
    if (dt < minDate) return getSalonMinDateMessage();
    if (seen.indexOf(dt) !== -1) return getSalonDateDuplicateMessage();
    if (toMinutes(en) - toMinutes(st) < SALON_MIN_RANGE_MINUTES) return getSalonTimeRangeInvalidMessage();
    seen.push(dt);
  }
  return seen.length ? '' : getSalonTimeRequiredMessage();
}

// Wires the interview-request form. Shown only when the customer can submit:
// new / Lite (canSubmitRequest) OR Rejected (re-apply). Hidden for Requesting /
// Reviewing (one request per user). On submit: POST the gate record (/sale/gachi/request),
// then the Zendesk ticket; success → success message, failure → toast (R1).
function setupSalonRequestForm(popup, sku, status) {
  const form = popup.querySelector('.salon-request-form');
  const success = popup.querySelector('.salon-form-success');
  if (success) success.style.display = 'none';
  if (!form) return;

  const requestStatus = status && status.requestStatus;
  const showForm = !!(status && (status.canSubmitRequest === true || requestStatus === 'Rejected'));

  if (!showForm || !sku) {
    form.style.display = 'none';
    return;
  }

  form.reset();
  const emailInput = form.querySelector('[name="email"]');
  if (emailInput && window.customerEmail) emailInput.value = window.customerEmail;
  // 各希望日: 日付に下限(翌日起算2営業日後) → 開始/終了 select 充填 → 終了選択肢の制限初期化 → 変更イベント委譲。
  const minBookingDate = getSalonMinBookingDate();
  form.querySelectorAll('.salon-day-date').forEach(function (di) { di.min = minBookingDate; });
  form.querySelectorAll('.salon-day-start, .salon-day-end').forEach(function (ts) { fillTimeOptions(ts); ts.selectedIndex = 0; });
  form.querySelectorAll('.salon-daygroup').forEach(function (g) { updateSalonEndOptions(g); });
  form.onchange = function (e) { handleSalonFieldChange(form, e.target); };
  form.style.display = '';

  // Reassigning onsubmit replaces any handler from a previous open (no stacking).
  form.onsubmit = function (event) { return handleSalonFormSubmit(event, form, popup, sku); };
}

async function handleSalonFormSubmit(event, form, popup, sku) {
  event.preventDefault();
  if (typeof form.checkValidity === 'function' && !form.checkValidity()) {
    if (typeof form.reportValidity === 'function') form.reportValidity();
    return;
  }

   // 希望日時の一括検証（fail-loud。native min/required バイパス対策込み）。
  const timeslotsError = getSalonTimeslotsError(form, getSalonMinBookingDate());
  if (timeslotsError) { showToast(timeslotsError, 'error'); return; }
  const timeslots = collectSalonTimeslots(form);

  const submitBtn = form.querySelector('button[type="submit"]');
  if (submitBtn) submitBtn.setAttribute('disabled', 'true');

  const getVal = function (name) {
    const el = form.querySelector('[name="' + name + '"]');
    return el ? String(el.value || '').trim() : '';
  };
  
  const fields = {
    email: getVal('email') || window.customerEmail || '',
    full_name: getVal('fullName'),
    phone_number: getVal('phone'),
    occupation: getVal('occupation'),
    reason: getVal('reason'),
    timeslots: timeslots,
  };

  // Bound the request so a hung API cannot trap the user (timeout → treated as failure
  // so they can retry). Confirmed failure (null) keeps the form and shows a toast (R1).
  const SALON_FORM_TIMEOUT_MS = 10000;
  const SALON_FORM_TIMED_OUT = Symbol('salon-form-timeout');
  let result = null;
  try {
    // 両方送る (both required): (1) the gate "Requesting" record via /sale/gachi/request
    // (idempotent), THEN (2) the Zendesk ticket via the new endpoint. The Zendesk
    // endpoint does NOT create the gate record, so step (1) is mandatory. Gate-first
    // (idempotent) avoids duplicate Zendesk tickets on retry.
    const submitBoth = (async function () {
      const gate = await submitGachiRequest(sku);
      if (!gate) return null;
      const ticket = await submitGachiForm(sku, fields);
      return ticket ? ticket : null;
    })();
    const raced = await Promise.race([
      submitBoth,
      new Promise(function (resolve) { setTimeout(function () { resolve(SALON_FORM_TIMED_OUT); }, SALON_FORM_TIMEOUT_MS); }),
    ]);
    result = (raced === SALON_FORM_TIMED_OUT) ? null : raced;
  } catch (err) {
    console.error('Failed to submit Gachi interview form:', err);
    result = null;
  }

  if (!result) {
    showToast(getSalonRequestFailedMessage(), 'error');
    if (submitBtn) submitBtn.removeAttribute('disabled');
    return;
  }

  // Success → hide the form + all body text, leaving only the success message.
  form.style.display = 'none';
  const defaultMsg = popup.querySelector('.salon-default-message');
  if (defaultMsg) defaultMsg.style.display = 'none';
  const statusMsg = popup.querySelector('.salon-status-message');
  if (statusMsg) statusMsg.style.display = 'none';
  const restricted = popup.querySelector('.salon-restricted-items');
  if (restricted) restricted.style.display = 'none';
  const success = popup.querySelector('.salon-form-success');
  if (success) success.style.display = '';

  // Show the Zendesk ticket number ("問い合わせ番号：◯◯") so the customer can quote it
  // in follow-ups. result = submitGachiForm's return ({ status, data: { ticket: { id, url } } }).
  const ticketId = result && result.data && result.data.ticket && result.data.ticket.id;
  const ticketEl = popup.querySelector('.salon-form-ticket');
  const ticketIdEl = popup.querySelector('.salon-form-ticket__id');
  if (ticketId && ticketEl && ticketIdEl) {
    ticketIdEl.textContent = String(ticketId);
    ticketEl.style.display = '';
  }
}

function getAgePriceRestrictionFromInventoryCheck(data) {
  const restriction = data && data.data ? data.data.age_price_restriction : null;
  if (!restriction || typeof restriction !== 'object') {
    return { blocked: false, reason: 'missing' };
  }
  return restriction;
}

function getPurchaseTierRestrictionFromInventoryCheck(data) {
  const restriction = data && data.data ? data.data.purchase_tier_restriction : null;
  if (!restriction || typeof restriction !== 'object') {
    return { blocked: false, reason: 'missing' };
  }
  return restriction;
}

function getPurchaseTierRestrictionMessage(restriction) {
  const reason = restriction.reason || 'purchase_tier_restricted';
  const hasPending = reason === 'limit_exceeded' && Number(restriction.pending_purchased_qty) > 0;
  const messageKey = hasPending ? 'limit_exceeded_with_pending' : reason;
  const template = (window.purchase_message && window.purchase_message[messageKey])
    ? window.purchase_message[messageKey]
    : reason;
  const placeholderValue = reason === 'tier_locked'
    ? restriction.locked_tier
    : (reason === 'limit_exceeded' ? restriction.tier_limit : restriction.tier);
  return template
    .replace(/\{X\}/g, String(placeholderValue))
    .replace(/\{Y\}/g, String(restriction.pending_purchased_qty ?? ''));
}

async function inventoryCheck() {
  try {
    // Get cart data to extract SKUs
    const cartData = await getSection('cart-data');
    const response = JSON.parse(cartData);

    if (!response.items || response.items.length === 0) {
      console.log('No items in cart for inventory check');
      return null;
    }

    // Aggregate quantities by SKU first
    const aggregatedCartItems = {};
        // Bundle re-validation (AIAOP-59): set when a cart line carries properties._bundle_sku,
    // stamped by product-form.js's applyBundleEligibility() at Add-to-Cart time. Re-checked
    // here per-checkout because subscription/eligibility state can change between the two calls.
    let bundleSku = null;
    // Bundle cart-integrity check: properties._bundle_resolved_skus records the full SKU set the
    // backend approved at Add-to-Cart time (same value on every line of that bundle). If the
    // customer manually removes one component line from the cart afterward, `actual` won't cover
    // `expected` anymore — caught below without needing another backend round-trip.
    const bundleGroups = {};

    response.items.forEach(cartItem => {
      if (cartItem.sku) {
        if (aggregatedCartItems[cartItem.sku]) {
          aggregatedCartItems[cartItem.sku].quantity += cartItem.quantity;
          // Keep the first title we encounter for this SKU
        } else {
          aggregatedCartItems[cartItem.sku] = {
            sku: cartItem.sku,
            name: cartItem.title,
            price: cartItem.price,
            quantity: cartItem.quantity
          };
        }
      }

      if (cartItem.properties && cartItem.properties._bundle_sku) {
        const groupSku = cartItem.properties._bundle_sku;
        if (!bundleSku) bundleSku = groupSku;

        if (!bundleGroups[groupSku]) {
          bundleGroups[groupSku] = { expected: new Set(), actual: new Set() };
        }
        if (cartItem.properties._bundle_resolved_skus) {
          String(cartItem.properties._bundle_resolved_skus)
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
            .forEach((s) => bundleGroups[groupSku].expected.add(s));
        }
        bundleGroups[groupSku].actual.add(cartItem.sku);
      }
    });

    // A bundle is incomplete if any component the backend approved at Add-to-Cart is no longer
    // present among the current cart lines carrying that bundle's tag (customer removed one).
    let bundleIncomplete = false;
    Object.keys(bundleGroups).forEach((groupSku) => {
      const { expected, actual } = bundleGroups[groupSku];
      const isMissingAComponent = [...expected].some((sku) => !actual.has(sku));
      if (isMissingAComponent) bundleIncomplete = true;
    });

    // Convert aggregated cart items to the new format expected by the API
     // AIAOP-48: include price so the backend can evaluate age/price restrictions.
    const inventoryProducts = Object.values(aggregatedCartItems).map(item => ({
      sku: item.sku,
      qty: item.quantity,
      price: (Number(item.price) || 0) / 100
    }));

    if (inventoryProducts.length === 0) {
      console.log('No SKUs found in cart items');
      return null;
    }

    // Build request body in format:
    // { email: "...", product: [ { sku, qty }, ... ] }
    const inventoryRequestData = {
      email: window.customerEmail || '',
      product: inventoryProducts
    };

    if (bundleSku) {
      inventoryRequestData.bundle_sku = bundleSku;
    }

    const inventoryResponse = await fetch(`${window.middleware_api_url}/suzu/common/v2/inventory-check`, {
      method: 'POST',
      body: JSON.stringify(inventoryRequestData),
      headers: {
        'Content-Type': 'application/json',
        'Accept-Language': Shopify.locale,
        'X-Sig': window.sz_config.middleware_key,
        'X-Epoch': window.sz_config.timestamp
      }
    });

    const data = await inventoryResponse.json();
      // Only meaningful when bundleSku was sent; getBundleResolutionItems fails closed on any
    // unexpected shape, so this must not be evaluated (and must not block checkout) otherwise.
    const bundleResolution = bundleSku ? getBundleResolutionItems(data) : null;

    const activeSubs = (data && data.data && data.data.active_subs) ? data.data.active_subs : [];
    const agePriceRestriction = getAgePriceRestrictionFromInventoryCheck(data);
    const purchaseTierRestriction = getPurchaseTierRestrictionFromInventoryCheck(data);
    const outOfStockItems = [];

    if (data && data.data && Array.isArray(data.data.out_stocks) && data.data.out_stocks.length > 0) {
      // The API now returns an array of SKUs that are out of stock
      const outOfStockSkus = data.data.out_stocks;
      const matchedSkus = new Set();

      Object.values(aggregatedCartItems).forEach(cartItem => {
        // Check if this cart item's SKU is in the out of stock list
        if (outOfStockSkus.includes(cartItem.sku)) {
          matchedSkus.add(cartItem.sku);
          outOfStockItems.push({
            sku: cartItem.sku,
            name: cartItem.name,
            requested_quantity: cartItem.quantity,
            available_quantity: 0 // Since it's out of stock, available quantity is 0
          });
        }
      });

      // AIAOP-49: the backend already scopes out_stocks to SKUs related to the
      // requested cart items (campaign bonus SKUs, original_sku sibling groups,
      // related-sku config expansion). Any SKU here that doesn't literally match a
      // cart SKU is still a related item that is out of stock, so it must block checkout too.
      outOfStockSkus.forEach(sku => {
        if (!matchedSkus.has(sku)) {
          outOfStockItems.push({
            sku: sku,
            name: sku,
            requested_quantity: 0,
            available_quantity: 0
          });
        }
      });
    }

    let salonBlocked = false;
    let salonGachiSku = null;
    let salonGachiStatus = null;
    let salonRestrictedItems = [];
    const gateSkus = getGachiGateSkus();
    const gachiItems = Object.values(aggregatedCartItems).filter(item => gateSkus.includes(item.sku));
    if (gachiItems.length > 0) {
      salonRestrictedItems = gachiItems.map(item => ({ sku: item.sku, name: item.name }));
      salonGachiSku = gachiItems[0].sku;
      salonGachiStatus = await getGachiStatus(gachiItems[0].sku);
      salonBlocked = !!(salonGachiStatus && salonGachiStatus.decision === 'block_checkout');
    }

    return {
      inventory: data,
      outOfStockItems: outOfStockItems,
      activeSubs: activeSubs,
      agePriceRestriction: agePriceRestriction,
      purchaseTierRestriction: purchaseTierRestriction,
      salonBlocked: salonBlocked,
      salonGachiSku: salonGachiSku,
      salonGachiStatus: salonGachiStatus,
      salonRestrictedItems: salonRestrictedItems,
      bundleBlocked: !!(bundleResolution && bundleResolution.blocked) || bundleIncomplete,
      bundleBlockReason: bundleIncomplete ? 'bundle_incomplete' : (bundleResolution ? bundleResolution.blockReason : null)

    };
  } catch (err) {
    console.error('Error in inventoryCheck:', err);
  }
  return null;
}

async function purchaseCheck() {
  try {
    if (!window.setting_show_purchase_limit) {
      console.log('setting_show_purchase_limit',window.setting_show_purchase_limit);
      return null;
    }

    // Get cart data to extract SKUs
    const cartData = await getSection('cart-data');
    const response = JSON.parse(cartData);

    if (!response.items || response.items.length === 0) {
      console.log('No items in cart for inventory check');
      return null;
    }

    // Aggregate quantities by SKU first
    const aggregatedCartItems = {};

    response.items.forEach(cartItem => {
      if (cartItem.sku) {
        if (aggregatedCartItems[cartItem.sku]) {
          aggregatedCartItems[cartItem.sku].quantity += cartItem.quantity;
          // Keep the first title we encounter for this SKU
        } else {
          aggregatedCartItems[cartItem.sku] = {
            sku: cartItem.sku,
            name: cartItem.title,
            price: cartItem.price,
            quantity: cartItem.quantity
          };
        }
      }
    });

    // Convert aggregated cart items to the new format
    const products = Object.values(aggregatedCartItems).map(item => ({
      sku: item.sku,
      qty: item.quantity,
      price: item.price / 100
    }));


    if (products.length === 0) {
      console.log('No SKUs found in cart items');
      return null;
    }

    // Prepare request data according to the new API format
    const requestData = {
      email: window.customerEmail || '',
      base_ccy: Shopify.currency.active || '',
      products: products
    };

    const inventoryResponse = await fetch(`${window.middleware_api_url}/ams/common/v1/validate-purchase`, {
      method: 'POST',
      body: JSON.stringify(requestData),
      headers: {
        'Content-Type': 'application/json',
        'Accept-Language': Shopify.locale
      }
    });

    const data = await inventoryResponse.json();

    // Check if the request was successful
    if (data.status === true) {
      // Purchase validation passed
      return {
        success: true,
        message: data.message,
        data: data.data
      };
    } else {
      // Purchase validation failed
       // AIAOP-48: age/price validator responses can omit validates.config, so
      // guard against it to avoid throwing and breaking checkout gating.
      const config = (data && data.validates && data.validates.config) ? data.validates.config : {};

      return {
        success: false,
        message: data && data.message ? data.message : 'purchase_validation_failed',
        limit_value_jpy: config.limit_value_jpy ?? null,
        limit_duration_days: config.limit_duration_days ?? null,
        data: data ? data.data : null
      };
    }
  } catch (err) {
    console.error('Error in purchaseCheck:', err);
  }
  return null;
}

async function getSaleInfo(customerId) {
  try {
    if (!customerId) {
      return { success: false, exists: false };
    }

    const saleInfoApiUrl = `${window.shopify_server_url}/sale/user-sale-info?customerId=${customerId}`;

    const response = await fetch(saleInfoApiUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Origin': window.location.origin,
        'Accept-Language': Shopify.locale || 'ja',
        'X-Sig': window.sz_config?.shopify_server_key,
        'X-Epoch': window.sz_config?.timestamp
      }
    });

    const result = await response.json();

    if (response.ok && result && (result.id || result.customerId || result.ssoId)) {
      return { success: true, exists: true, data: result };
    } else if (response.status === 404 || !result || (!result.id && !result.customerId && !result.ssoId)) {
      return { success: true, exists: false };
    } else {
      console.warn('Failed to get sale info:', result);
      return { success: false, exists: false, error: result };
    }
  } catch (error) {
    console.error('Error getting sale info:', error);
    return { success: false, exists: false, error: error.message };
  }
}

async function saveIsReceivingContract(isReceivingContract) {
  try {
    const customerId = window.meta?.page?.customerId;
    if (!customerId) {
      console.warn('Cannot save isReceivingContract: customerId not found');
      return { success: false };
    }

    const saleInfoApiUrl = `${window.shopify_server_url}/sale/user-sale-info`;

    var authToken = getCookie(window.ENV == "DEV"? 'auth-token-test' : 'auth-token');


    // Check if sale info already exists
    const existingSaleInfo = await getSaleInfo(customerId);
    const isUpdate = existingSaleInfo.success && existingSaleInfo.exists;

    // Prepare request body with only isReceivingContract
    const requestBody = {
      customerId: customerId,
      isReceivingContract: isReceivingContract
    };

    // Add ssoId and email if updating existing record
    if (isUpdate && existingSaleInfo.data) {
      requestBody.ssoId = existingSaleInfo.data.ssoId;
      requestBody.email = existingSaleInfo.data.email || window.customerEmail || '';
    } else {
      var authJson = JSON.parse(authToken);
      // For new record, need ssoId and email
      requestBody.ssoId = authJson?.user?.id || null;
      requestBody.email = window.customerEmail || '';
    }

    const response = await fetch(saleInfoApiUrl, {
      method: isUpdate ? 'PUT' : 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Origin': window.location.origin,
        'Accept-Language': Shopify.locale || 'ja',
        'X-Sig': window.sz_config?.shopify_server_key,
        'X-Epoch': window.sz_config?.timestamp
      },
      body: JSON.stringify(requestBody)
    });

    const result = await response.json();

    if (response.ok && result) {
      return { success: true, data: result };
    } else {
      console.warn('Failed to save isReceivingContract:', result);
      return { success: false, error: result };
    }
  } catch (error) {
    console.error('Error saving isReceivingContract:', error);
    return { success: false, error: error.message };
  }
}


// True only when EVERY item in the cart is a no-contract item (option B).
// A mixed cart (any item that still requires a contract) returns false, so the
// contract step is kept. A failed product fetch is treated as "not no-contract"
// so we never wrongly drop the contract.
async function cartIsAllNoContract() {
  try {
      const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      const cart = await res.json();
      if (!cart.items?.length) return false;
      const handles = [...new Set(cart.items.map(i => i.handle).filter(Boolean))];
      if (!handles.length) return false;
      const tagsLists = await Promise.all(handles.map(h =>
          fetch(`/products/${h}.js`).then(r => r.json()).then(p => p.tags || []).catch(() => null)
      ));
      return tagsLists.every(tags => Array.isArray(tags) && tags.some(t => String(t).toLowerCase() === 'no-contract'));
  } catch (e) {
      console.error('cartIsAllNoContract failed:', e);
      return false;
  }
}


async function initReceivingContractState() {
  const checkbox = document.querySelector('#is_receiving_contract');
  if (!checkbox) return;

  const customerId = window.meta?.page?.customerId;
  if (!customerId) return;

  try {
     const noContractCart = await cartIsAllNoContract();
    if (noContractCart) {
            checkbox.checked = false;
            checkbox.disabled = true;
        } else {
    const saleInfo = await getSaleInfo(customerId);
    if (saleInfo.success && saleInfo.exists && saleInfo.data) {
      if (typeof saleInfo.data.isReceivingContract !== 'undefined') {
        if(saleInfo.data.isReceivingContract) {
          checkbox.checked = !!saleInfo.data.isReceivingContract;
        }

        checkbox.dispatchEvent(new Event('change', { bubbles: true }));
      }
      }
    }

    // Add event listener to save isReceivingContract when checkbox changes
    if (!checkbox.hasAttribute('data-has-save-listener')) {
      checkbox.setAttribute('data-has-save-listener', 'true');
      checkbox.addEventListener('change', async (e) => {
        const isChecked = e.target.checked;
        await saveIsReceivingContract(isChecked);
      });
    }
  } catch (error) {
    console.error('Failed to initialize receiving contract checkbox:', error);
  }
}

document.addEventListener('DOMContentLoaded', initReceivingContractState);